// ============================================================
// TIMECARD APP v2 - Complete Single-File React Application
// Stack: React + Supabase
//
// SETUP: Replace SUPABASE_URL and SUPABASE_ANON_KEY below.
// Also run the v2_migration.sql in Supabase SQL Editor first!
// Hi, Alyssa! : P 
// ============================================================

import { useState, useEffect, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";

// ── CONFIG ─────────────────────────────────────────────────
const SUPABASE_URL = "https://objvfnzzfwurumgaiqxl.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_OOsgH0kVDk45SP7XgR2QPA_SIAK-oBz";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const HOURS_OPTIONS = [
  0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 9, 10,
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── STYLES ─────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --blue: #1a6fc4;
    --blue-dark: #155a9e;
    --blue-pale: #e8f0fa;
    --blue-mid: #ccddf5;
    --green: #2e7d32;
    --green-pale: #e8f5e9;
    --red: #c62828;
    --red-pale: #ffebee;
    --orange: #e65100;
    --orange-pale: #fff3e0;
    --purple: #5c35a0;
    --purple-pale: #ede7f6;
    --gray-50: #f8f9fa;
    --gray-100: #f1f3f4;
    --gray-200: #e8eaed;
    --gray-300: #dadce0;
    --gray-400: #bdc1c6;
    --gray-500: #80868b;
    --gray-600: #5f6368;
    --gray-700: #3c4043;
    --gray-900: #202124;
    --border: #dadce0;
    --font: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }
  body { font-family: var(--font); background: var(--gray-100); color: var(--gray-900); font-size: 13px; min-height: 100vh; }

  /* AUTH */
  .auth-wrap { min-height:100vh; display:flex; align-items:center; justify-content:center; background: #1a6fc4; overflow:hidden; }
  .auth-card { background:rgba(255,255,255,0.15); backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px); border:1px solid rgba(255,255,255,0.3); padding:40px 36px; width:100%; max-width:400px; box-shadow: 0 8px 32px rgba(0,0,0,0.2); }
  .auth-logo { font-size:20px; font-weight:600; color:white; margin-bottom:4px; letter-spacing:-0.3px; }
  .auth-sub { font-size:12px; color:rgba(255,255,255,0.8); margin-bottom:28px; }
  .auth-tabs { display:flex; margin-bottom:20px; border-bottom:1px solid rgba(255,255,255,0.3); }
  .auth-tab { flex:1; padding:9px; text-align:center; cursor:pointer; font-family:var(--font); font-size:12px; font-weight:500; color:rgba(255,255,255,0.6); border:none; background:transparent; transition:all 0.15s; }
  .auth-tab.active { color:white; border-bottom:2px solid white; margin-bottom:-1px; }
  .field label { display:block; font-size:11px; font-weight:600; color:rgba(255,255,255,0.8); margin-bottom:5px; text-transform:uppercase; letter-spacing:0.04em; }
  .field input, .field select { width:100%; padding:7px 10px; border:1px solid rgba(255,255,255,0.3); background:rgba(255,255,255,0.15); font-family:var(--font); font-size:13px; color:white; outline:none; border-radius:2px; transition:border-color 0.1s; }
  .field input::placeholder { color:rgba(255,255,255,0.5); }
  .field input:focus, .field select:focus { border-color:white; background:rgba(255,255,255,0.25); }
  .field select option { background:var(--blue-dark); color:white; }
  .pending-notice { background:rgba(255,255,255,0.15); border:1px solid rgba(255,255,255,0.4); color:white; padding:14px; font-size:13px; line-height:1.6; margin-top:16px; border-radius:2px; }

  /* FORMS */
  .field { margin-bottom:14px; }
  .field label { display:block; font-size:11px; font-weight:600; color:var(--gray-600); margin-bottom:5px; text-transform:uppercase; letter-spacing:0.04em; }
  .field input, .field select { width:100%; padding:7px 10px; border:1px solid var(--gray-300); background:white; font-family:var(--font); font-size:13px; color:var(--gray-900); outline:none; border-radius:2px; transition:border-color 0.1s; }
  .field input:focus, .field select:focus { border-color:var(--blue); box-shadow: 0 0 0 2px var(--blue-mid); }

  .btn { padding:7px 16px; border:1px solid transparent; cursor:pointer; font-family:var(--font); font-size:12px; font-weight:500; border-radius:2px; transition:all 0.1s; background:transparent; }
  .btn-primary { background:var(--blue); color:white; border-color:var(--blue); }
  .btn-primary:hover { background:var(--blue-dark); border-color:var(--blue-dark); }
  .btn-full { width:100%; padding:9px 16px; font-size:13px; }
  .btn-success { background:var(--green); color:white; border-color:var(--green); }
  .btn-success:hover { background:#1b5e20; }
  .btn-danger { background:var(--red); color:white; border-color:var(--red); }
  .btn-danger:hover { background:#b71c1c; }
  .btn-purple { background:var(--purple); color:white; border-color:var(--purple); }
  .btn-purple:hover { background:#4a2980; }
  .btn-outline { background:white; color:var(--gray-700); border-color:var(--gray-300); }
  .btn-outline:hover { background:var(--gray-100); }
  .btn-sm { padding:4px 10px; font-size:11px; }
  .btn:disabled { opacity:0.5; cursor:not-allowed; }

  /* SHELL */
  .shell { min-height:100vh; display:flex; flex-direction:column; }
  .topbar { background:var(--blue); color:white; padding:0 20px; height:44px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; }
  .topbar-logo { font-size:15px; font-weight:600; color:white; letter-spacing:-0.2px; }
  .topbar-right { display:flex; align-items:center; gap:16px; }
  .topbar-user { font-size:12px; color:rgba(255,255,255,0.85); }
  .topbar-role { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; padding:2px 8px; border-radius:10px; background:rgba(255,255,255,0.2); color:white; }
  .topbar-role.administrator { background:rgba(255,255,255,0.25); }
  .topbar-role.manager { background:rgba(255,255,255,0.2); }
  .topbar-signout { font-size:12px; color:rgba(255,255,255,0.8); background:none; border:1px solid rgba(255,255,255,0.3); cursor:pointer; padding:3px 10px; border-radius:2px; transition:all 0.1s; font-family:var(--font); }
  .topbar-signout:hover { background:rgba(255,255,255,0.15); color:white; }

  .nav { background:white; border-bottom:1px solid var(--border); padding:0 20px; display:flex; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
  .nav-item { padding:11px 16px; font-size:12px; font-weight:500; cursor:pointer; border-bottom:2px solid transparent; color:var(--gray-600); border-top:none; border-left:none; border-right:none; background:transparent; font-family:var(--font); transition:all 0.1s; }
  .nav-item.active { color:var(--blue); border-bottom:2px solid var(--blue); }
  .nav-item:hover:not(.active) { color:var(--gray-900); background:var(--gray-50); }
  .nav-badge { background:var(--red); color:white; font-size:10px; font-weight:600; padding:1px 5px; border-radius:8px; margin-left:4px; vertical-align:middle; }
  .main { flex:1; padding:24px; max-width:1600px; margin:0 auto; width:100%; }

  /* PAGE HEADERS */
  .page-header { margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid var(--border); }
  .page-title { font-size:18px; font-weight:600; color:var(--gray-900); }
  .page-sub { font-size:12px; color:var(--gray-500); margin-top:3px; }

  /* CALENDAR */
  .cal-controls { display:flex; align-items:center; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
  .cal-nav { display:flex; align-items:center; gap:8px; }
  .cal-nav-btn { width:28px; height:28px; border:1px solid var(--border); background:white; cursor:pointer; font-size:14px; display:flex; align-items:center; justify-content:center; border-radius:2px; transition:all 0.1s; color:var(--gray-600); }
  .cal-nav-btn:hover { border-color:var(--blue); color:var(--blue); background:var(--blue-pale); }
  .cal-month { font-size:16px; font-weight:600; color:var(--gray-900); min-width:180px; }
  .cal-status { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; padding:3px 10px; border-radius:10px; }
  .cal-status.approved { background:var(--green-pale); color:var(--green); }
  .cal-status.unapproved { background:var(--orange-pale); color:var(--orange); }
  .cal-status.submitted { background:var(--blue-pale); color:var(--blue); }
  .cal-status.closed { background:#fce4ec; color:#880e4f; }
  .approved-banner { background:var(--green-pale); border:1px solid #a5d6a7; color:var(--green); padding:8px 14px; font-size:12px; margin-bottom:14px; border-radius:2px; }
  .submitted-banner { background:var(--blue-pale); border:1px solid var(--blue-mid); color:var(--blue); padding:8px 14px; font-size:12px; margin-bottom:14px; border-radius:2px; }
  .warning-banner { background:#fff8e1; border:1px solid #ffe082; color:#f57f17; padding:10px 14px; font-size:12px; margin-bottom:14px; border-radius:2px; display:flex; align-items:center; gap:8px; }
  .closed-banner { background:#fce4ec; border:1px solid #f48fb1; color:#880e4f; padding:8px 14px; font-size:12px; margin-bottom:14px; border-radius:2px; }
  .badge-submitted { background:var(--blue-pale); color:var(--blue); }
  .badge-closed { background:#fce4ec; color:#880e4f; }
  .cal-grid { display:grid; grid-template-columns:repeat(7,1fr); border:1px solid var(--border); background:var(--border); gap:1px; border-radius:2px; overflow:hidden; }
  .cal-day-header { background:var(--gray-700); color:white; padding:7px; text-align:center; font-size:11px; font-weight:600; letter-spacing:0.05em; text-transform:uppercase; }
  .cal-cell { background:white; padding:8px; min-height:110px; display:flex; flex-direction:column; gap:4px; }
  .cal-cell.empty { background:var(--gray-50); }
  .cal-cell.weekend { background:#fafafa; }
  .cal-cell.today { outline:2px solid var(--blue); outline-offset:-2px; }
  .cal-date { font-size:12px; font-weight:500; color:var(--gray-500); display:flex; justify-content:space-between; align-items:center; margin-bottom:2px; }
  .cal-date-num { font-size:13px; font-weight:600; color:var(--gray-700); }
  .cal-total { font-size:11px; color:var(--green); font-weight:600; }
  .entry-row { display:flex; gap:4px; align-items:center; padding:3px 6px; background:var(--blue-pale); border-left:2px solid var(--blue); border-radius:0 2px 2px 0; }
  .entry-hours { font-size:11px; font-weight:600; color:var(--blue); min-width:26px; }
  .entry-code { font-size:11px; color:var(--gray-600); flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .entry-del { background:none; border:none; cursor:pointer; color:var(--gray-400); font-size:13px; line-height:1; padding:0 2px; transition:color 0.1s; }
  .entry-del:hover { color:var(--red); }
  .add-entry-btn { background:none; border:1px dashed var(--gray-300); cursor:pointer; font-size:11px; color:var(--gray-400); padding:3px; text-align:center; transition:all 0.1s; width:100%; border-radius:2px; margin-top:auto; }
  .add-entry-btn:hover { border-color:var(--blue); color:var(--blue); background:var(--blue-pale); }

  /* MODALS */
  .overlay { position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:200; }
  .modal { background:white; border:1px solid var(--border); padding:28px; width:100%; max-width:420px; box-shadow:0 4px 20px rgba(0,0,0,0.15); border-radius:2px; }
  .modal-title { font-size:16px; font-weight:600; margin-bottom:18px; color:var(--gray-900); }
  .modal-actions { display:flex; gap:8px; margin-top:20px; justify-content:flex-end; }

  /* TABLES */
  .tbl-wrap { overflow-x:auto; border:1px solid var(--border); border-radius:2px; }
  table { width:100%; border-collapse:collapse; }
  th { text-align:left; padding:9px 12px; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; background:var(--gray-100); color:var(--gray-600); border-bottom:1px solid var(--border); }
  td { padding:8px 12px; border-bottom:1px solid var(--gray-200); font-size:13px; vertical-align:middle; }
  tr:last-child td { border-bottom:none; }
  tr:hover td { background:var(--blue-pale); }
  .badge { font-size:10px; font-weight:600; letter-spacing:0.04em; text-transform:uppercase; padding:2px 8px; border-radius:10px; white-space:nowrap; display:inline-block; }
  .badge-approved { background:var(--green-pale); color:var(--green); }
  .badge-unapproved { background:var(--orange-pale); color:var(--orange); }
  .badge-pending { background:var(--purple-pale); color:var(--purple); }
  .badge-active { background:var(--green-pale); color:var(--green); }
  .badge-inactive { background:var(--gray-100); color:var(--gray-500); }

  /* CARDS */
  .card { background:white; border:1px solid var(--border); padding:20px; margin-bottom:14px; border-radius:2px; }
  .card-title { font-size:14px; font-weight:600; margin-bottom:14px; color:var(--gray-900); }

  /* AUDIT */
  .audit-item { padding:10px 0; border-bottom:1px solid var(--gray-200); display:flex; gap:14px; align-items:flex-start; }
  .audit-time { font-size:11px; color:var(--gray-500); min-width:150px; }
  .audit-actor { font-size:12px; font-weight:600; color:var(--blue); min-width:130px; }
  .audit-action { font-size:12px; color:var(--gray-700); }

  /* REPORT */
  .report-controls { display:flex; gap:10px; margin-bottom:20px; flex-wrap:wrap; align-items:flex-end; padding:16px; background:white; border:1px solid var(--border); border-radius:2px; }
  .report-summary { display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:12px; margin-bottom:20px; }
  .stat-card { background:white; border:1px solid var(--border); padding:16px; border-radius:2px; border-top:3px solid var(--blue); }
  .stat-val { font-size:28px; font-weight:300; color:var(--blue); line-height:1; }
  .stat-lbl { font-size:11px; font-weight:600; color:var(--gray-500); text-transform:uppercase; letter-spacing:0.04em; margin-top:6px; }

  /* ADMIN TABS */
  .admin-tabs { display:flex; margin-bottom:20px; border-bottom:1px solid var(--border); background:white; }
  .admin-tab { padding:10px 18px; font-size:12px; font-weight:500; cursor:pointer; color:var(--gray-600); border:none; background:transparent; font-family:var(--font); transition:all 0.1s; border-bottom:2px solid transparent; margin-bottom:-1px; }
  .admin-tab.active { color:var(--blue); border-bottom:2px solid var(--blue); }
  .admin-tab:hover:not(.active) { color:var(--gray-900); background:var(--gray-50); }
  .admin-compact table { font-size: 12px; }
.admin-compact th, .admin-compact td { padding: 6px 8px; }
.admin-compact select, .admin-compact input { padding: 4px 6px; font-size: 11px; }
.admin-compact .action-cell { gap: 3px; }
.admin-compact .btn-sm { padding: 3px 7px; font-size: 10px; }

  /* MISC */
  .error { background:var(--red-pale); border:1px solid #ef9a9a; color:#b71c1c; padding:9px 13px; font-size:12px; margin-bottom:14px; border-radius:2px; }
  .success { background:var(--green-pale); border:1px solid #a5d6a7; color:#1b5e20; padding:9px 13px; font-size:12px; margin-bottom:14px; border-radius:2px; }
  .info { background:var(--blue-pale); border:1px solid var(--blue-mid); color:#0d47a1; padding:9px 13px; font-size:12px; margin-bottom:14px; border-radius:2px; line-height:1.6; }
  .loading { text-align:center; padding:60px; color:var(--gray-500); font-size:13px; }
  .empty { text-align:center; padding:40px; color:var(--gray-500); font-size:13px; }

  /* THINK TANK */
  .tt-composer { background:white; border:1px solid var(--border); padding:20px; margin-bottom:20px; border-radius:2px; }
  .tt-composer textarea { width:100%; padding:10px 12px; border:1px solid var(--border); font-family:var(--font); font-size:14px; color:var(--gray-900); outline:none; resize:vertical; min-height:80px; border-radius:2px; transition:border-color 0.1s; }
  .tt-composer textarea:focus { border-color:var(--blue); box-shadow:0 0 0 2px var(--blue-mid); }
  .tt-composer-actions { display:flex; justify-content:flex-end; margin-top:10px; }
  .tt-post { background:white; border:1px solid var(--border); border-radius:2px; margin-bottom:12px; }
  .tt-post-header { display:flex; align-items:center; gap:10px; padding:14px 16px 0; }
  .tt-avatar { width:34px; height:34px; border-radius:50%; background:var(--blue); color:white; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; flex-shrink:0; }
  .tt-author { font-size:13px; font-weight:600; color:var(--gray-900); }
  .tt-time { font-size:11px; color:var(--gray-400); margin-left:auto; }
  .tt-role-badge { font-size:10px; font-weight:600; text-transform:uppercase; padding:1px 6px; border-radius:8px; background:var(--blue-pale); color:var(--blue); }
  .tt-content { padding:10px 16px 14px; font-size:14px; color:var(--gray-800); line-height:1.6; white-space:pre-wrap; }
  .tt-actions { display:flex; align-items:center; gap:4px; padding:8px 16px; border-top:1px solid var(--gray-200); }
  .tt-action-btn { background:none; border:none; cursor:pointer; font-family:var(--font); font-size:12px; color:var(--gray-500); padding:4px 10px; border-radius:2px; transition:all 0.1s; display:flex; align-items:center; gap:5px; }
  .tt-action-btn:hover { background:var(--gray-100); color:var(--gray-900); }
  .tt-action-btn.liked { color:var(--red); font-weight:600; }
  .tt-action-btn.liked:hover { background:var(--red-pale); }
  .tt-delete-btn { margin-left:auto; color:var(--gray-400); }
  .tt-delete-btn:hover { color:var(--red); background:var(--red-pale); }
  .tt-comments { background:var(--gray-50); border-top:1px solid var(--gray-200); padding:12px 16px; }
  .tt-comment { display:flex; gap:10px; margin-bottom:10px; align-items:flex-start; }
  .tt-comment-avatar { width:26px; height:26px; border-radius:50%; background:var(--gray-300); color:var(--gray-700); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:600; flex-shrink:0; margin-top:2px; }
  .tt-comment-body { flex:1; }
  .tt-comment-author { font-size:12px; font-weight:600; color:var(--gray-900); margin-right:6px; }
  .tt-comment-time { font-size:11px; color:var(--gray-400); }
  .tt-comment-text { font-size:13px; color:var(--gray-700); margin-top:3px; line-height:1.5; }
  .tt-comment-input { display:flex; gap:8px; margin-top:10px; align-items:center; }
  .tt-comment-input input { flex:1; padding:7px 10px; border:1px solid var(--border); border-radius:2px; font-family:var(--font); font-size:13px; outline:none; }
  .tt-comment-input input:focus { border-color:var(--blue); }
  .tt-empty { text-align:center; padding:60px; color:var(--gray-400); font-size:14px; }
  .action-cell { display:flex; gap:5px; flex-wrap:wrap; align-items:center; }
  code { background:var(--gray-100); padding:1px 5px; border-radius:2px; font-size:12px; color:var(--gray-700); }

  /* KIOSK MODE */
  .kiosk-wrap { min-height:100vh; background:#1a1a2e; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; }
  .kiosk-card { background:white; border-radius:12px; padding:40px; width:100%; max-width:480px; box-shadow:0 8px 40px rgba(0,0,0,0.3); }
  .kiosk-logo { font-size:22px; font-weight:700; color:var(--blue); margin-bottom:4px; }
  .kiosk-sub { font-size:13px; color:var(--gray-500); margin-bottom:28px; }
  .kiosk-name { font-size:28px; font-weight:600; color:var(--gray-900); margin-bottom:4px; text-align:center; }
  .kiosk-time { font-size:48px; font-weight:300; color:var(--blue); text-align:center; margin:16px 0; letter-spacing:-1px; }
  .kiosk-date { font-size:16px; color:var(--gray-500); text-align:center; margin-bottom:24px; }
  .kiosk-pin-display { display:flex; gap:10px; justify-content:center; margin:20px 0; }
  .kiosk-pin-dot { width:16px; height:16px; border-radius:50%; border:2px solid var(--blue); background:transparent; transition:background 0.1s; }
  .kiosk-pin-dot.filled { background:var(--blue); }
  .kiosk-numpad { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin:20px 0; }
  .kiosk-key { padding:20px; font-size:22px; font-weight:500; border:1px solid var(--border); border-radius:8px; background:var(--gray-50); cursor:pointer; text-align:center; transition:all 0.1s; color:var(--gray-900); }
  .kiosk-key:hover { background:var(--blue-pale); border-color:var(--blue); }
  .kiosk-key:active { background:var(--blue); color:white; transform:scale(0.97); }
  .kiosk-key.danger { color:var(--red); border-color:var(--red-pale); }
  .kiosk-key.danger:hover { background:var(--red-pale); }
  .kiosk-status { display:flex; flex-direction:column; gap:12px; }
  .kiosk-action-btn { padding:18px; font-size:16px; font-weight:600; border:none; border-radius:8px; cursor:pointer; transition:all 0.15s; width:100%; }
  .kiosk-action-btn.punch-in { background:var(--green); color:white; }
  .kiosk-action-btn.punch-out { background:var(--red); color:white; }
  .kiosk-action-btn.break-start { background:var(--orange); color:white; }
  .kiosk-action-btn.break-end { background:var(--blue); color:white; }
  .kiosk-action-btn:disabled { opacity:0.5; cursor:not-allowed; }
  .kiosk-punched-in { background:var(--green-pale); border:1px solid #a5d6a7; border-radius:8px; padding:14px; text-align:center; color:var(--green); font-weight:600; margin-bottom:12px; }
  .kiosk-attestation { background:var(--gray-50); border:1px solid var(--border); border-radius:8px; padding:20px; }
  .kiosk-attestation h3 { font-size:16px; font-weight:600; margin-bottom:16px; color:var(--gray-900); }
  .kiosk-check { display:flex; align-items:flex-start; gap:12px; margin-bottom:14px; cursor:pointer; }
  .kiosk-check input { width:20px; height:20px; margin-top:2px; cursor:pointer; flex-shrink:0; }
  .kiosk-check label { font-size:14px; color:var(--gray-700); cursor:pointer; line-height:1.5; }

  /* PTO */
  .pto-type-badge { font-size:10px; font-weight:600; text-transform:uppercase; padding:2px 8px; border-radius:10px; }
  .pto-vacation { background:#e3f2fd; color:#1565c0; }
  .pto-sick { background:#fce4ec; color:#880e4f; }
  .pto-bereavement { background:#ede7f6; color:#4527a0; }
  .pto-unpaid { background:var(--gray-100); color:var(--gray-600); }
  .pto-pending { background:var(--orange-pale); color:var(--orange); }
  .pto-approved { background:var(--green-pale); color:var(--green); }
  .pto-denied { background:var(--red-pale); color:var(--red); }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .auth-wrap img { display:none; }
    .auth-card { margin:0 !important; max-width:100% !important; padding:32px 24px; }
    .topbar { padding:0 16px; height:48px; }
    .topbar-logo { font-size:16px; }
    .topbar-user { display:none; }
    .topbar-role { font-size:9px; padding:2px 6px; }
    .topbar-signout { font-size:11px; padding:3px 8px; }
    .nav { padding:0 8px; overflow-x:auto; -webkit-overflow-scrolling:touch; }
    .nav::-webkit-scrollbar { display:none; }
    .nav-item { padding:10px 12px; font-size:11px; white-space:nowrap; }
    .main { padding:16px 12px; }
    .page-title { font-size:20px; }
    .cal-grid { display:flex; flex-direction:column; gap:0; }
    .cal-day-header { display:none; }
    .cal-cell { min-height:auto; padding:10px 12px; border-bottom:1px solid var(--gray-200); }
    .cal-cell.empty { display:none; }
    .cal-cell.weekend { background:white; }
    .cal-date-num { font-size:13px; color:var(--blue); font-weight:600; }
    .cal-controls { gap:8px; }
    .cal-month { font-size:15px; min-width:unset; }
    .cal-nav-btn { width:28px; height:28px; }
    .btn { padding:8px 14px; font-size:12px; }
    .btn-sm { padding:5px 10px; font-size:11px; }
    .btn-full { width:100%; }
    .tbl-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
    table { min-width:500px; }
    th, td { padding:8px 10px; font-size:12px; }
    .card { padding:16px; }
    .cal-controls { flex-wrap:wrap; }
    .report-summary { grid-template-columns:1fr 1fr; }
    .stat-val { font-size:24px; }
    .admin-tabs { overflow-x:auto; -webkit-overflow-scrolling:touch; }
    .admin-tabs::-webkit-scrollbar { display:none; }
    .admin-tab { white-space:nowrap; padding:10px 14px; }
    .report-controls { flex-direction:column; align-items:stretch; }
    .report-controls .field { margin-bottom:8px !important; }
    .report-controls select { width:100%; }
    .tt-composer { padding:14px; }
    .tt-post-header { padding:12px 12px 0; }
    .tt-content { padding:8px 12px 12px; font-size:13px; }
    .tt-actions { padding:6px 12px; }
    .tt-comments { padding:10px 12px; }
    .overlay { align-items:flex-end; }
    .modal { max-width:100% !important; border-radius:8px 8px 0 0; border-left:none; border-right:none; border-bottom:none; max-height:90vh; overflow-y:auto; }
    .approved-banner, .submitted-banner, .warning-banner, .closed-banner { font-size:11px; padding:8px 10px; }
    .action-cell { gap:4px; }
  }

  @media (max-width: 480px) {
    .topbar-role { display:none; }
    .report-summary { grid-template-columns:1fr; }
    .cal-nav { gap:6px; }
    .cal-month { font-size:14px; }
  }
`;

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}
function firstDayOfMonth(year, month) {
  return new Date(year, month - 1, 1).getDay();
}
function fmtDate(d) {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
function fmtDateTime(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
function padDate(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function isToday(y, m, d) {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() + 1 === m && t.getDate() === d;
}

// ── APP ROOT ───────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadProfile(session.user.id, session.user.user_metadata);
      else setLoading(false);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_ev, session) => {
      console.log(
        "auth event:",
        _ev,
        "userMeta:",
        session?.user?.user_metadata
      );
      setSession(session);
      if (session) loadProfile(session.user.id, session.user.user_metadata);
      else {
        setProfile(null);
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(uid, userMeta) {
    console.log("userMeta:", userMeta); // temporary debug line
    console.log("AUTH UID:", uid);
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", uid)
      .single();

    const oktaRole = userMeta?.custom_claims?.role || userMeta?.role;
    if (
      data &&
      oktaRole &&
      ["employee", "manager", "administrator"].includes(oktaRole) &&
      data.role !== oktaRole
    ) {
      await supabase.from("profiles").update({ role: oktaRole }).eq("id", uid);
      data.role = oktaRole;
    } // Auto-activate Okta users
    if (
      data &&
      data.status === "pending" &&
      userMeta?.iss?.includes("okta.com")
    ) {
      await supabase
        .from("profiles")
        .update({ status: "active" })
        .eq("id", uid);
      data.status = "active";
    }

    setProfile(data);
    setLoading(false);
  }

  if (loading)
    return (
      <>
        <StyleTag />
        <div className="loading">Loading…</div>
      </>
    );

  if (new URLSearchParams(window.location.search).get("kiosk") === "1") {
    return (
      <>
        <StyleTag />
        <KioskPage />
      </>
    );
  }

  if (!session)
    return (
      <>
        <StyleTag />
        <AuthPage />
      </>
    );
  if (!profile)
    return (
      <>
        <StyleTag />
        <div className="loading">Setting up profile…</div>
      </>
    );

  if (profile.status === "inactive")
    return (
      <>
        <StyleTag />
        <div className="auth-wrap">
          <div className="auth-card">
            <div className="auth-logo">Time With Joe!</div>
            <div className="auth-sub">
              <em>Timecards that don't suck</em>
            </div>
            <div className="error" style={{ marginTop: 16 }}>
              <strong>Account Deactivated</strong>
              <br />
              Your account has been deactivated. Please contact your
              administrator.
            </div>
            <br />
            <button
              className="btn btn-danger btn-full"
              onClick={() => supabase.auth.signOut()}
            >
              Sign Out
            </button>
          </div>
        </div>
      </>
    );

  return (
    <>
      <StyleTag />
      <Shell profile={profile} onProfileUpdate={setProfile} />
    </>
  );
}

function StyleTag() {
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
function AuthPage() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function handleOktaSignIn() {
    setBusy(true);
    setErr("");
    const { data, error } = await supabase.auth.signInWithSSO({
      domain: "visby.com",
    });
    if (error) {
      setErr(error.message);
      setBusy(false);
    } else if (data?.url) {
      window.location.href = data.url;
    }
  }

  async function handleAdminSignIn(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) setErr(error.message);
    setBusy(false);
  }

  return (
    <div className="auth-wrap">
      <img
        src="https://i.imgur.com/aRSM93z.png"
        style={{
          position: "fixed",
          bottom: "0",
          right: "0",
          height: "90vh",
          width: "auto",
          objectFit: "contain",
          objectPosition: "bottom right",
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
          mixBlendMode: "multiply",
        }}
      />
      <div
        className="auth-card"
        style={{
          position: "relative",
          zIndex: 1,
          marginRight: "auto",
          marginLeft: "10%",
        }}
      >
        <div className="auth-logo">Time With Joe!</div>
        <div className="auth-sub">
          <em>Timecards that don't suck</em>
        </div>
        {err && <div className="error">{err}</div>}
        <button
          className="btn btn-primary btn-full"
          onClick={handleOktaSignIn}
          disabled={busy}
        >
          {busy ? "Redirecting…" : "Sign in with Okta"}
        </button>
        {!showAdminLogin ? (
          <p
            style={{
              textAlign: "center",
              marginTop: "24px",
              fontSize: "12px",
              color: "#aaa",
              cursor: "pointer",
            }}
            onClick={() => setShowAdminLogin(true)}
          >
            Admin access
          </p>
        ) : (
          <form onSubmit={handleAdminSignIn} style={{ marginTop: "24px" }}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="btn btn-primary btn-full" disabled={busy}>
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
// ── SHELL ──────────────────────────────────────────────────
function Shell({ profile, onProfileUpdate }) {
  const [page, setPage] = useState("timecard");
  const [pendingCount, setPendingCount] = useState(0);
  const [drillEmployee, setDrillEmployee] = useState(null);

  useEffect(() => {
    if (profile.role === "administrator") loadPendingCount();
  }, [profile.role]);

  async function loadPendingCount() {
    const { count } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .eq("status", "pending");
    setPendingCount(count || 0);
  }

  const navItems = [
    ...(profile.worker_type !== "hourly"
      ? [{ id: "timecard", label: "My Timecard" }]
      : []),
    ...(profile.role === "manager" || profile.role === "administrator"
      ? [{ id: "myemployees", label: "My Employees" }]
      : []),
    ...(profile.role === "manager"
      ? [{ id: "team", label: "Team Timecards" }]
      : []),
    { id: "pto", label: "PTO" },
    { id: "thinktank", label: "Think Tank" },
    ...(profile.role !== "employee"
      ? [{ id: "report", label: "Reports" }]
      : []),
    ...(profile.role === "administrator"
      ? [{ id: "admin", label: "Admin", badge: pendingCount }]
      : []),
  ];

  function handleNavClick(id) {
    setPage(id);
    setDrillEmployee(null);
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div></div>
          <div className="topbar-logo">TWJ</div>
        </div>
        <div className="topbar-right">
          <span className="topbar-user">{profile.full_name}</span>
          <span className={`topbar-role ${profile.role}`}>{profile.role}</span>
          <button
            className="topbar-signout"
            onClick={() => supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </header>
      <nav className="nav">
        {navItems.map((n) => (
          <button
            key={n.id}
            className={`nav-item ${page === n.id ? "active" : ""}`}
            onClick={() => handleNavClick(n.id)}
          >
            {n.label}
            {n?.badge > 0 && <span className="nav-badge">{n.badge}</span>}
          </button>
        ))}
      </nav>
      <main className="main">
        {page === "timecard" && <TimecardPage profile={profile} />}
        {page === "myemployees" &&
          !drillEmployee &&
          (profile.role === "manager" || profile.role === "administrator") && (
            <MyEmployeesPage
              profile={profile}
              onDrill={(emp) => setDrillEmployee(emp)}
            />
          )}
        {page === "myemployees" && drillEmployee && (
          <div>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setDrillEmployee(null)}
              style={{ marginBottom: 20 }}
            >
              ← Back to My Employees
            </button>
            <TimecardPage
              key={drillEmployee.id}
              profile={profile}
              employeeId={drillEmployee.id}
            />
          </div>
        )}
        {page === "team" && profile.role === "manager" && (
          <TeamPage profile={profile} />
        )}
        {page === "pto" && <PTOPage profile={profile} />}
        {page === "thinktank" && <ThinkTankPage profile={profile} />}
        {page === "report" && profile.role !== "employee" && (
          <ReportPage profile={profile} />
        )}
        {page === "admin" && profile.role === "administrator" && (
          <AdminPage
            profile={profile}
            onProfileUpdate={onProfileUpdate}
            onPendingChange={loadPendingCount}
          />
        )}
      </main>
    </div>
  );
}

// ── TIMECARD PAGE ──────────────────────────────────────────
function TimecardPage({ profile, employeeId }) {
  const targetId = employeeId || profile.id;
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [timecard, setTimecard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(null);
  const [auditModal, setAuditModal] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [employeeName, setEmployeeName] = useState("");
  const [periodSignoff, setPeriodSignoff] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const isApproved = timecard?.status === "approved";
  const isSubmitted = timecard?.status === "submitted";
  const isPeriodClosed = !!periodSignoff;
  const isOwnCard = targetId === profile.id;
  const canEdit = !isApproved && !isPeriodClosed && isOwnCard;
  const canApprove =
    (profile.role === "manager" || profile.role === "administrator") &&
    !isOwnCard &&
    !isPeriodClosed;

  const weekdayCount = useMemo(() => {
    let count = 0;
    const d = daysInMonth(year, month);
    for (let i = 1; i <= d; i++) {
      const day = new Date(year, month - 1, i).getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }, [year, month]);
  const expectedHours = weekdayCount * 8;

  useEffect(() => {
    loadProjects();
  }, []);
  useEffect(() => {
    loadTimecard();
  }, [year, month, targetId]);

  async function loadProjects() {
    const { data } = await supabase
      .from("projects")
      .select("id,code,name,active")
      .order("code");
    setProjects((data || []).filter((p) => p.active === true));
  }

  async function loadTimecard() {
    setLoading(true);
    if (!isOwnCard) {
      const { data: emp } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", targetId)
        .single();
      if (emp) setEmployeeName(emp.full_name);
    }
    const { data: signoff } = await supabase
      .from("period_signoffs")
      .select("*, signer:signed_off_by(full_name)")
      .eq("year", year)
      .eq("month", month)
      .maybeSingle();
    setPeriodSignoff(signoff || null);

    const monthStr = `${year}-${String(month).padStart(2, "0")}`;
    const { data: hols } = await supabase
      .from("holidays")
      .select("*")
      .gte("date", `${monthStr}-01`)
      .lte("date", `${monthStr}-${new Date(year, month, 0).getDate()}`);
    setHolidays(hols || []);

    const { data: tcData } = await supabase
      .from("timecards")
      .select("*, approver:approved_by(full_name)")
      .eq("employee_id", targetId)
      .eq("year", year)
      .eq("month", month)
      .maybeSingle();
    setTimecard(tcData || null);
    if (tcData) {
      const { data: ents } = await supabase
        .from("timecard_entries")
        .select("*, projects(code,name)")
        .eq("timecard_id", tcData.id)
        .order("entry_date");
      setEntries(ents || []);
    } else setEntries([]);
    setLoading(false);
  }

  async function ensureTimecard() {
    if (timecard) return timecard;
    const { data, error } = await supabase
      .from("timecards")
      .insert({ employee_id: targetId, year, month, status: "unapproved" })
      .select()
      .single();
    if (error) throw error;
    setTimecard(data);
    return data;
  }

  async function addEntry(date, hours, projectId) {
    const tc = await ensureTimecard();
    const { data, error } = await supabase
      .from("timecard_entries")
      .insert({
        timecard_id: tc.id,
        entry_date: date,
        hours,
        project_id: projectId,
      })
      .select("*, projects(code,name)")
      .single();
    if (error) throw error;
    setEntries((prev) => [...prev, data]);
    const proj = projects.find((p) => p.id === projectId);
    await writeAudit(
      tc.id,
      "entry_added",
      `${hours}h on ${date} (${proj?.code})`
    );
  }

  async function deleteEntry(entry) {
    await supabase.from("timecard_entries").delete().eq("id", entry.id);
    setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    await writeAudit(
      timecard.id,
      "entry_deleted",
      `${entry.hours}h on ${entry.entry_date} (${entry.projects?.code})`
    );
  }

  async function writeAudit(tcId, action, detail) {
    await supabase.from("audit_log").insert({
      timecard_id: tcId,
      actor_id: profile.id,
      actor_name: profile.full_name,
      action,
      detail,
    });
  }

  async function loadAudit() {
    if (!timecard) return;
    const { data } = await supabase
      .from("audit_log")
      .select("*")
      .eq("timecard_id", timecard.id)
      .order("created_at", { ascending: false });
    setAuditLog(data || []);
    setAuditModal(true);
  }

  async function submitTimecard() {
    if (totalHours < expectedHours) {
      const shortage = (expectedHours - totalHours).toFixed(1);
      if (
        !window.confirm(
          `You have ${totalHours.toFixed(
            1
          )}h logged but ${expectedHours}h are expected for this month (${weekdayCount} weekdays × 8h). You are ${shortage}h short.\n\nIf you have time off, make sure you have submitted your PTO before submitting your timecard.\n\nSubmit anyway?`
        )
      )
        return;
    }
    const tc = await ensureTimecard();
    const tcId = tc.id;
    const { data } = await supabase
      .from("timecards")
      .update({ status: "submitted" })
      .eq("id", tcId)
      .select("*, approver:approved_by(full_name)")
      .maybeSingle();
    if (data) setTimecard(data);
    await writeAudit(
      tcId,
      "submitted",
      `Submitted for approval by ${profile.full_name}`
    );
    try {
      const { data: empProfile } = await supabase
        .from("profiles")
        .select("manager_id, full_name")
        .eq("id", profile.id)
        .single();
      if (empProfile?.manager_id) {
        const { data: mgr } = await supabase
          .from("profiles")
          .select("email, full_name")
          .eq("id", empProfile.manager_id)
          .single();
        if (mgr?.email) {
          await supabase.functions.invoke("notify-manager", {
            body: {
              to: mgr.email,
              managerName: mgr.full_name,
              employeeName: profile.full_name,
              month: MONTH_NAMES[month - 1],
              year,
            },
          });
        }
      }
    } catch (e) {
      /* Email optional */
    }
  }

  async function unsubmitTimecard() {
    const tcId = timecard.id;
    const { data } = await supabase
      .from("timecards")
      .update({ status: "unapproved" })
      .eq("id", tcId)
      .select("*, approver:approved_by(full_name)")
      .maybeSingle();
    if (data) setTimecard(data);
    await writeAudit(
      tcId,
      "unsubmitted",
      `Submission recalled by ${profile.full_name}`
    );
  }

  async function approveTimecard() {
    const tcId = timecard.id;
    const { data } = await supabase
      .from("timecards")
      .update({
        status: "approved",
        approved_by: profile.id,
        approved_at: new Date().toISOString(),
      })
      .eq("id", tcId)
      .select("*, approver:approved_by(full_name)")
      .maybeSingle();
    if (data) setTimecard(data);
    await writeAudit(tcId, "approved", `Approved by ${profile.full_name}`);
  }

  async function unapproveTimecard() {
    const tcId = timecard.id;
    const { data } = await supabase
      .from("timecards")
      .update({ status: "unapproved", approved_by: null, approved_at: null })
      .eq("id", tcId)
      .select("*, approver:approved_by(full_name)")
      .single();
    if (data) setTimecard(data);
    await writeAudit(tcId, "unapproved", `Unapproved by ${profile.full_name}`);
  }

  function prevMonth() {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  }
  function nextMonth() {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  }

  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!map[e.entry_date]) map[e.entry_date] = [];
      map[e.entry_date].push(e);
    });
    return map;
  }, [entries]);

  const totalHours = entries.reduce((s, e) => s + parseFloat(e.hours), 0);
  const todayDate = new Date();
  const isCurrentMonth =
    todayDate.getFullYear() === year && todayDate.getMonth() + 1 === month;
  const weekdaysElapsed = useMemo(() => {
    if (!isCurrentMonth) return weekdayCount;
    let count = 0;
    const t = todayDate.getDate();
    for (let i = 1; i <= t; i++) {
      const day = new Date(year, month - 1, i).getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }, [year, month, isCurrentMonth]);
  const proratedExpected = weekdaysElapsed * 8;
  const showHoursWarning =
    isOwnCard &&
    weekdaysElapsed > 0 &&
    totalHours < proratedExpected &&
    totalHours >= 0;

  if (loading) return <div className="loading">Loading timecard…</div>;

  const firstDay = firstDayOfMonth(year, month);
  const days = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">
          {employeeName ? `${employeeName}'s Timecard` : "My Timecard"}
        </div>
        <div className="page-sub">
          Total this month: {totalHours.toFixed(1)} hours
        </div>
      </div>

      {isPeriodClosed && (
        <div className="closed-banner">
          🔒 <strong>Period Closed</strong> — {MONTH_NAMES[month - 1]} {year}{" "}
          was signed off by{" "}
          <strong>{periodSignoff.signer?.full_name || "Administrator"}</strong>{" "}
          on {fmtDateTime(periodSignoff.signed_off_at)}. No further edits or
          approvals are permitted.
        </div>
      )}
      {isApproved && !isPeriodClosed && (
        <div className="approved-banner">
          ✓ Approved by{" "}
          <strong>{timecard.approver?.full_name || "Manager"}</strong> on{" "}
          {fmtDateTime(timecard.approved_at)} — this timecard is locked and
          cannot be edited.
        </div>
      )}
      {isSubmitted && !isApproved && (
        <div className="submitted-banner">
          ⏳ Submitted for approval — you can still edit this timecard until
          your manager approves it.
        </div>
      )}
      {showHoursWarning && (
        <div className="warning-banner">
          ⚠️ <strong>Hours trending low:</strong> Based on weekdays elapsed,
          you're expected to have {proratedExpected.toFixed(1)}h logged — you
          have {totalHours.toFixed(1)}h. If you have time off, make sure you've
          submitted your PTO.
        </div>
      )}

      <div className="cal-controls">
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={prevMonth}>
            ‹
          </button>
          <div className="cal-month">
            {MONTH_NAMES[month - 1]} {year}
          </div>
          <button className="cal-nav-btn" onClick={nextMonth}>
            ›
          </button>
        </div>
        <span
          className={`cal-status ${
            isPeriodClosed ? "closed" : timecard?.status || "unapproved"
          }`}
        >
          {isPeriodClosed
            ? "🔒 Period Closed"
            : timecard?.status === "approved"
            ? "✓ Approved"
            : timecard?.status === "submitted"
            ? "⏳ Submitted"
            : "Pending Approval"}
        </span>
        {timecard && (
          <button
            className="btn btn-sm btn-outline"
            onClick={loadAudit}
            style={{ marginLeft: "auto" }}
          >
            Audit Trail
          </button>
        )}
        {canEdit && !isSubmitted && (
          <button className="btn btn-sm btn-primary" onClick={submitTimecard}>
            Submit for Approval
          </button>
        )}
        {canEdit && isSubmitted && (
          <button className="btn btn-sm btn-outline" onClick={unsubmitTimecard}>
            Recall Submission
          </button>
        )}
        {canApprove &&
          timecard &&
          (isApproved ? (
            <button
              className="btn btn-sm btn-danger"
              onClick={unapproveTimecard}
            >
              Unapprove
            </button>
          ) : (
            <button
              className="btn btn-sm btn-success"
              onClick={approveTimecard}
            >
              Approve Timecard
            </button>
          ))}
      </div>

      <div className="cal-grid">
        {DAY_NAMES.map((d) => (
          <div key={d} className="cal-day-header">
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} className="cal-cell empty" />;
          const dateStr = padDate(year, month, day);
          const dayEntries = entriesByDate[dateStr] || [];
          const dayTotal = dayEntries.reduce(
            (s, e) => s + parseFloat(e.hours),
            0
          );
          const isWeekend = [0, 6].includes(
            new Date(year, month - 1, day).getDay()
          );
          return (
            <div
              key={day}
              className={`cal-cell ${isWeekend ? "weekend" : ""} ${
                isToday(year, month, day) ? "today" : ""
              }`}
            >
              <div className="cal-date">
                <span className="cal-date-num">{day}</span>
                {dayTotal > 0 && (
                  <span className="cal-total">{dayTotal.toFixed(1)}h</span>
                )}
              </div>
              {dayEntries.map((e) => (
                <div key={e.id} className="entry-row">
                  <span className="entry-hours">{e.hours}h</span>
                  <span className="entry-code" title={e.projects?.name}>
                    {e.projects?.code}
                  </span>
                  {canEdit && (
                    <button
                      className="entry-del"
                      onClick={() => deleteEntry(e)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {holidays
                .filter((h) => h.date === dateStr)
                .map((h) => (
                  <div
                    key={h.id}
                    className="entry-row"
                    style={{
                      borderLeftColor: "var(--purple)",
                      background: "var(--purple-pale)",
                    }}
                  >
                    <span
                      className="entry-hours"
                      style={{ color: "var(--purple)" }}
                    >
                      {h.hours}h
                    </span>
                    <span
                      className="entry-code"
                      style={{ color: "var(--purple)" }}
                      title={h.name}
                    >
                      🎉 {h.name}
                    </span>
                  </div>
                ))}
              {canEdit && (
                <button
                  className="add-entry-btn"
                  onClick={() =>
                    setAddModal({ date: dateStr, existingHours: dayTotal })
                  }
                >
                  + add
                </button>
              )}
            </div>
          );
        })}
      </div>

      {addModal && (
        <AddEntryModal
          date={addModal.date}
          projects={projects}
          existingDayHours={addModal.existingHours || 0}
          onSave={async (hours, projectId) => {
            await addEntry(addModal.date, hours, projectId);
            setAddModal(null);
          }}
          onClose={() => setAddModal(null)}
        />
      )}
      {auditModal && (
        <AuditModal log={auditLog} onClose={() => setAuditModal(false)} />
      )}
    </div>
  );
}

function AddEntryModal({ date, projects, onSave, onClose, existingDayHours }) {
  const [hours, setHours] = useState("8");
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const remainingHours = 24 - (existingDayHours || 0);
  const availableHours = HOURS_OPTIONS.filter((h) => h <= remainingHours);

  async function handleSave() {
    if (!projectId) {
      setErr("Select a project");
      return;
    }
    const newTotal = (existingDayHours || 0) + parseFloat(hours);
    if (newTotal > 24) {
      setErr(
        `Cannot exceed 24 hours in a day. You have ${(
          existingDayHours || 0
        ).toFixed(1)}h already logged.`
      );
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await onSave(parseFloat(hours), projectId);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  return (
    <div
      className="overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal">
        <div className="modal-title">
          Add Hours — {fmtDate(new Date(date + "T12:00:00"))}
        </div>
        {existingDayHours > 0 && (
          <div className="info" style={{ marginBottom: 12 }}>
            Already logged {existingDayHours.toFixed(1)}h today — max{" "}
            {remainingHours.toFixed(1)}h remaining.
          </div>
        )}
        {err && <div className="error">{err}</div>}
        <div className="field">
          <label>Hours</label>
          <select value={hours} onChange={(e) => setHours(e.target.value)}>
            {availableHours.length > 0 ? (
              availableHours.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))
            ) : (
              <option disabled>No hours remaining today</option>
            )}
          </select>
        </div>
        <div className="field">
          <label>Project Code</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            {projects
              .filter((p) => p.active !== false)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
          </select>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={busy}
          >
            {busy ? "Saving…" : "Add Entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditModal({ log, onClose }) {
  return (
    <div
      className="overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-title">Audit Trail</div>
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            borderTop: "1px solid var(--border)",
            borderBottom: "1px solid var(--border)",
            margin: "0 -32px",
            padding: "0 32px",
          }}
        >
          {log.length === 0 ? (
            <div className="empty">No audit records yet.</div>
          ) : (
            log.map((item) => (
              <div key={item.id} className="audit-item">
                <span className="audit-time">
                  {fmtDateTime(item.created_at)}
                </span>
                <span className="audit-actor">{item.actor_name}</span>
                <span className="audit-action">
                  <strong>{item.action}</strong>
                  {item.detail ? ` — ${item.detail}` : ""}
                </span>
              </div>
            ))
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MY EMPLOYEES PAGE ─────────────────────────────────────
function MyEmployeesPage({ profile, onDrill }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    loadData();
  }, [year, month]);

  async function loadData() {
    setLoading(true);
    let empQuery = supabase
      .from("profiles")
      .select("id,full_name,external_id,email")
      .eq("status", "active");
    if (profile.role !== "administrator") {
      empQuery = empQuery.eq("manager_id", profile.id);
    }
    const { data: emps } = await empQuery;
    if (!emps || emps.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    const empIds = emps.map((e) => e.id);
    const { data: tcs } = await supabase
      .from("timecards")
      .select("id,employee_id,status,approved_at,approved_by")
      .in("employee_id", empIds)
      .eq("year", year)
      .eq("month", month);
    const tcIds = (tcs || []).map((t) => t.id);
    const { data: entries } =
      tcIds.length > 0
        ? await supabase
            .from("timecard_entries")
            .select("timecard_id,hours,projects(code,name)")
            .in("timecard_id", tcIds)
        : { data: [] };
    const result = emps.map((emp) => {
      const tc = (tcs || []).find((t) => t.employee_id === emp.id);
      const empEntries = tc
        ? (entries || []).filter((e) => e.timecard_id === tc.id)
        : [];
      const byProject = {};
      empEntries.forEach((e) => {
        const key = e.projects?.code || "?";
        if (!byProject[key])
          byProject[key] = {
            code: e.projects?.code,
            name: e.projects?.name,
            hours: 0,
          };
        byProject[key].hours += parseFloat(e.hours);
      });
      return {
        emp,
        tc,
        totalHours: empEntries.reduce((s, e) => s + parseFloat(e.hours), 0),
        byProject: Object.values(byProject),
        status: tc?.status || "none",
      };
    });
    setRows(result);
    setLoading(false);
  }

  async function approveTimecard(tc) {
    setBusy(tc.id);
    await supabase
      .from("timecards")
      .update({
        status: "approved",
        approved_by: profile.id,
        approved_at: new Date().toISOString(),
      })
      .eq("id", tc.id);
    await supabase.from("audit_log").insert({
      timecard_id: tc.id,
      actor_id: profile.id,
      actor_name: profile.full_name,
      action: "approved",
      detail: `Approved by ${profile.full_name}`,
    });
    setBusy(null);
    await loadData();
  }

  async function unapproveTimecard(tc) {
    setBusy(tc.id);
    await supabase
      .from("timecards")
      .update({ status: "unapproved", approved_by: null, approved_at: null })
      .eq("id", tc.id);
    await supabase.from("audit_log").insert({
      timecard_id: tc.id,
      actor_id: profile.id,
      actor_name: profile.full_name,
      action: "unapproved",
      detail: `Unapproved by ${profile.full_name}`,
    });
    setBusy(null);
    await loadData();
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">My Employees</div>
        <div className="page-sub">
          Current period hours by employee and project code
        </div>
      </div>
      <div className="report-controls">
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Year</label>
          <select
            className="inline"
            value={year}
            onChange={(e) => setYear(+e.target.value)}
          >
            {[
              today.getFullYear() - 1,
              today.getFullYear(),
              today.getFullYear() + 1,
            ].map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Month</label>
          <select
            className="inline"
            value={month}
            onChange={(e) => setMonth(+e.target.value)}
          >
            {MONTH_NAMES.map((n, i) => (
              <option key={i} value={i + 1}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading && <div className="loading">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="empty">
          {profile.role === "administrator"
            ? "No active employees found."
            : "No active employees assigned to you."}
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Ext. ID</th>
                <th>Project Code</th>
                <th>Project Name</th>
                <th>Hours</th>
                <th>Status</th>
                <th>Timecard</th>
                <th>Approval</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) =>
                row.byProject.length === 0 ? (
                  <tr key={row.emp.id}>
                    <td>{row.emp.full_name}</td>
                    <td>
                      <code>{row.emp.external_id || "—"}</code>
                    </td>
                    <td
                      colSpan={2}
                      style={{ color: "var(--gray-400)", fontStyle: "italic" }}
                    >
                      No entries this period
                    </td>
                    <td>0.0</td>
                    <td>
                      <span
                        className={`badge badge-${
                          row.status === "approved"
                            ? "approved"
                            : row.status === "unapproved"
                            ? "unapproved"
                            : "inactive"
                        }`}
                      >
                        {row.status === "none" ? "No timecard" : row.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onDrill(row.emp)}
                      >
                        View
                      </button>
                    </td>
                    <td>
                      {row.tc ? (
                        row.status === "approved" ? (
                          <button
                            className="btn btn-sm btn-danger"
                            disabled={busy === row.tc.id}
                            onClick={() => unapproveTimecard(row.tc)}
                          >
                            {busy === row.tc.id ? "…" : "Unapprove"}
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-success"
                            disabled={busy === row.tc.id}
                            onClick={() => approveTimecard(row.tc)}
                          >
                            {busy === row.tc.id ? "…" : "Approve"}
                          </button>
                        )
                      ) : (
                        <span
                          style={{ color: "var(--gray-400)", fontSize: 12 }}
                        >
                          No timecard
                        </span>
                      )}
                    </td>
                  </tr>
                ) : (
                  row.byProject.map((p, i) => (
                    <tr key={`${row.emp.id}-${p.code}`}>
                      {i === 0 && (
                        <td rowSpan={row.byProject.length}>
                          {row.emp.full_name}
                        </td>
                      )}
                      {i === 0 && (
                        <td rowSpan={row.byProject.length}>
                          <code>{row.emp.external_id || "—"}</code>
                        </td>
                      )}
                      <td>
                        <strong>{p.code}</strong>
                      </td>
                      <td>{p.name}</td>
                      <td>{p.hours.toFixed(1)}</td>
                      {i === 0 && (
                        <td rowSpan={row.byProject.length}>
                          <span
                            className={`badge badge-${
                              row.status === "approved"
                                ? "approved"
                                : row.status === "submitted"
                                ? "submitted"
                                : row.status === "unapproved"
                                ? "unapproved"
                                : "inactive"
                            }`}
                          >
                            {row.status === "none" ? "No timecard" : row.status}
                          </span>
                        </td>
                      )}
                      {i === 0 && (
                        <td rowSpan={row.byProject.length}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => onDrill(row.emp)}
                          >
                            View
                          </button>
                        </td>
                      )}
                      {i === 0 && (
                        <td rowSpan={row.byProject.length}>
                          {row.tc ? (
                            row.status === "approved" ? (
                              <button
                                className="btn btn-sm btn-danger"
                                disabled={busy === row.tc.id}
                                onClick={() => unapproveTimecard(row.tc)}
                              >
                                {busy === row.tc.id ? "…" : "Unapprove"}
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-success"
                                disabled={busy === row.tc.id}
                                onClick={() => approveTimecard(row.tc)}
                              >
                                {busy === row.tc.id ? "…" : "Approve"}
                              </button>
                            )
                          ) : (
                            <span
                              style={{ color: "var(--gray-400)", fontSize: 12 }}
                            >
                              No timecard
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── TEAM PAGE ──────────────────────────────────────────────
function TeamPage({ profile }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("profiles")
      .select("*")
      .eq("manager_id", profile.id)
      .eq("status", "active")
      .then(({ data }) => {
        setEmployees(data || []);
        setLoading(false);
      });
  }, [profile.id]);

  if (loading) return <div className="loading">Loading team…</div>;
  if (selected)
    return (
      <div>
        <button
          className="btn btn-sm"
          onClick={() => setSelected(null)}
          style={{ marginBottom: 20 }}
        >
          ← Back to Team
        </button>
        <TimecardPage profile={profile} employeeId={selected.id} />
      </div>
    );
  return (
    <div>
      <div className="page-header">
        <div className="page-title">Team Timecards</div>
        <div className="page-sub">
          {employees.length} active employee{employees.length !== 1 ? "s" : ""}{" "}
          on your team
        </div>
      </div>
      {employees.length === 0 ? (
        <div className="empty">No active employees assigned to you yet.</div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>External ID</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id}>
                  <td>{e.full_name}</td>
                  <td>
                    <code>{e.external_id || "—"}</code>
                  </td>
                  <td>{e.email}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setSelected(e)}
                    >
                      View Timecard
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── REPORT PAGE ────────────────────────────────────────────
function ReportPage({ profile }) {
  const today = new Date();
  const [reportTab, setReportTab] = useState("hours");
  const [rangeMode, setRangeMode] = useState("month");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);
  const [ptoRows, setPtoRows] = useState([]);
  const [ptoLoading, setPtoLoading] = useState(false);
  const [ptoRan, setPtoRan] = useState(false);
  const [ptoFrom, setPtoFrom] = useState("");
  const [ptoTo, setPtoTo] = useState("");
  const [ptoStatus, setPtoStatus] = useState("all");

  async function runReport() {
    setLoading(true);
    setRan(true);
    let tcQuery = supabase
      .from("timecards")
      .select(
        "id,status,approved_at,employee_id,profiles!timecards_employee_id_fkey(full_name,external_id,department,manager:manager_id(full_name)),approver:approved_by(full_name)"
      )
      .limit(50000);
    if (rangeMode === "month")
      tcQuery = tcQuery.eq("year", year).eq("month", month);
    if (profile.role === "manager") {
      const { data: myTeam } = await supabase
        .from("profiles")
        .select("id")
        .eq("manager_id", profile.id);
      const ids = (myTeam || []).map((e) => e.id);
      if (ids.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }
      tcQuery = tcQuery.in("employee_id", ids);
    }
    const { data: tcs } = await tcQuery;
    if (!tcs || tcs.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    const tcIds = tcs.map((t) => t.id);
    let entriesQuery = supabase
      .from("timecard_entries")
      .select("timecard_id,hours,entry_date,projects(code,name)")
      .in("timecard_id", tcIds)
      .limit(50000);
    if (rangeMode === "custom" && dateFrom)
      entriesQuery = entriesQuery.gte("entry_date", dateFrom);
    if (rangeMode === "custom" && dateTo)
      entriesQuery = entriesQuery.lte("entry_date", dateTo);
    const { data: entries } = await entriesQuery;
    const result = [];
    (entries || []).forEach((e) => {
      const tc = tcs.find((t) => t.id === e.timecard_id);
      if (!tc) return;
      result.push({
        name: tc.profiles.full_name,
        external_id: tc.profiles.external_id || "—",
        department: tc.profiles.department || "—",
        manager_name: tc.profiles.manager?.full_name || "Unassigned",
        entry_date: e.entry_date,
        project_code: e.projects?.code,
        project_name: e.projects?.name,
        status: tc.status,
        approved_at: tc.approved_at,
        approved_by_name: tc.approver?.full_name || "—",
        hours: parseFloat(e.hours),
      });
    });
    setRows(
      result.sort(
        (a, b) =>
          a.name.localeCompare(b.name) ||
          a.entry_date?.localeCompare(b.entry_date)
      )
    );
    setLoading(false);
  }

  async function runPtoReport() {
    setPtoLoading(true);
    setPtoRan(true);
    let q = supabase
      .from("pto_requests")
      .select(
        "*, employee:employee_id(full_name,external_id), reviewer:reviewed_by(full_name)"
      )
      .order("start_date", { ascending: false });
    if (ptoStatus !== "all") q = q.eq("status", ptoStatus);
    if (ptoFrom) q = q.gte("start_date", ptoFrom);
    if (ptoTo) q = q.lte("end_date", ptoTo);
    if (profile.role === "manager") {
      const { data: myTeam } = await supabase
        .from("profiles")
        .select("id")
        .eq("manager_id", profile.id);
      const ids = (myTeam || []).map((e) => e.id);
      if (ids.length === 0) {
        setPtoRows([]);
        setPtoLoading(false);
        return;
      }
      q = q.in("employee_id", ids);
    }
    const { data } = await q;
    setPtoRows(data || []);
    setPtoLoading(false);
  }

  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const approvedHours = rows
    .filter((r) => r.status === "approved")
    .reduce((s, r) => s + r.hours, 0);
  const headers = [
    "Employee",
    "External ID",
    "Department",
    "Date",
    "Project Code",
    "Project Name",
    "Hours",
    "Status",
    "Manager",
    "Approved By",
    "Approved At",
  ];
  const csvRows = rows.map((r) =>
    [
      `"${r.name}"`,
      `"${r.external_id}"`,
      `"${r.department}"`,
      `"${r.entry_date}"`,
      `"${r.project_code}"`,
      `"${r.project_name}"`,
      r.hours.toFixed(1),
      r.status,
      `"${r.manager_name}"`,
      `"${r.status === "approved" ? r.approved_by_name : "—"}"`,
      `"${fmtDateTime(r.approved_at)}"`,
    ].join(",")
  );
  function exportCSV() {
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timecard_report_${
      rangeMode === "month"
        ? `${MONTH_NAMES[month - 1]}_${year}`
        : `${dateFrom}_to_${dateTo}`
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportPtoCSV() {
    const headers = [
      "Employee",
      "Ext ID",
      "Type",
      "Start",
      "End",
      "Hours",
      "Status",
      "Reviewed By",
      "Note",
    ];
    const csvRows = ptoRows.map((r) =>
      [
        `"${r.employee?.full_name}"`,
        `"${r.employee?.external_id || "—"}"`,
        r.pto_type,
        r.start_date,
        r.end_date,
        r.total_hours,
        r.status,
        `"${r.reviewer?.full_name || "—"}"`,
        `"${r.review_note || ""}"`,
      ].join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pto_report_${ptoFrom || "all"}_to_${ptoTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const ptoColors = {
    vacation: "pto-vacation",
    sick: "pto-sick",
    bereavement: "pto-bereavement",
    unpaid: "pto-unpaid",
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Reports</div>
        <div className="page-sub">Hours and PTO reporting</div>
      </div>
      <div className="admin-tabs" style={{ marginBottom: 20 }}>
        <button
          className={`admin-tab ${reportTab === "hours" ? "active" : ""}`}
          onClick={() => setReportTab("hours")}
        >
          Hours Report
        </button>
        <button
          className={`admin-tab ${reportTab === "pto" ? "active" : ""}`}
          onClick={() => setReportTab("pto")}
        >
          PTO Report
        </button>
      </div>
      {reportTab === "hours" && (
        <>
          <div className="report-controls">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Range Type</label>
              <select
                className="inline"
                value={rangeMode}
                onChange={(e) => setRangeMode(e.target.value)}
              >
                <option value="month">By Month</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>
            {rangeMode === "month" && (
              <>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Year</label>
                  <select
                    className="inline"
                    value={year}
                    onChange={(e) => setYear(+e.target.value)}
                  >
                    {[
                      today.getFullYear() - 1,
                      today.getFullYear(),
                      today.getFullYear() + 1,
                    ].map((y) => (
                      <option key={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Month</label>
                  <select
                    className="inline"
                    value={month}
                    onChange={(e) => setMonth(+e.target.value)}
                  >
                    {MONTH_NAMES.map((n, i) => (
                      <option key={i} value={i + 1}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {rangeMode === "custom" && (
              <>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>From</label>
                  <input
                    type="date"
                    className="inline"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      padding: "4px 8px",
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                    }}
                  />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>To</label>
                  <input
                    type="date"
                    className="inline"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{
                      padding: "4px 8px",
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                    }}
                  />
                </div>
              </>
            )}
            <button className="btn btn-primary" onClick={runReport}>
              Run Report
            </button>
            {ran && !loading && rows.length > 0 && (
              <button
                className="btn btn-outline"
                onClick={exportCSV}
                style={{ marginLeft: "auto" }}
              >
                ⬇ Export CSV
              </button>
            )}
          </div>
          {ran && !loading && rows.length > 0 && (
            <div className="report-summary">
              <div className="stat-card">
                <div className="stat-val">{totalHours.toFixed(1)}</div>
                <div className="stat-lbl">Total Hours</div>
              </div>
              <div className="stat-card">
                <div className="stat-val">{approvedHours.toFixed(1)}</div>
                <div className="stat-lbl">Approved Hours</div>
              </div>
              <div className="stat-card">
                <div className="stat-val">
                  {new Set(rows.map((r) => r.name)).size}
                </div>
                <div className="stat-lbl">Employees</div>
              </div>
              <div className="stat-card">
                <div className="stat-val">
                  {new Set(rows.map((r) => r.project_code)).size}
                </div>
                <div className="stat-lbl">Projects</div>
              </div>
            </div>
          )}
          {loading && <div className="loading">Generating report…</div>}
          {ran && !loading && rows.length === 0 && (
            <div className="empty">No timecard data for this period.</div>
          )}
          {ran && !loading && rows.length > 0 && (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>External ID</th>
                    <th>Date</th>
                    <th>Project Code</th>
                    <th>Project Name</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Manager</th>
                    <th>Approved By</th>
                    <th>Approved At</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.name}</td>
                      <td>
                        <code>{r.external_id}</code>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.entry_date}</td>
                      <td>
                        <strong>{r.project_code}</strong>
                      </td>
                      <td>{r.project_name}</td>
                      <td>{r.hours.toFixed(1)}</td>
                      <td>
                        <span className={`badge badge-${r.status}`}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.manager_name}</td>
                      <td style={{ fontSize: 12 }}>
                        {r.status === "approved" ? r.approved_by_name : "—"}
                      </td>
                      <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                        {fmtDateTime(r.approved_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {reportTab === "pto" && (
        <>
          <div className="report-controls">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>From</label>
              <input
                type="date"
                className="inline"
                value={ptoFrom}
                onChange={(e) => setPtoFrom(e.target.value)}
                style={{
                  padding: "4px 8px",
                  border: "1px solid var(--border)",
                  borderRadius: 2,
                }}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>To</label>
              <input
                type="date"
                className="inline"
                value={ptoTo}
                onChange={(e) => setPtoTo(e.target.value)}
                style={{
                  padding: "4px 8px",
                  border: "1px solid var(--border)",
                  borderRadius: 2,
                }}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Status</label>
              <select
                className="inline"
                value={ptoStatus}
                onChange={(e) => setPtoStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={runPtoReport}>
              Run Report
            </button>
            {ptoRan && !ptoLoading && ptoRows.length > 0 && (
              <button
                className="btn btn-outline"
                onClick={exportPtoCSV}
                style={{ marginLeft: "auto" }}
              >
                ⬇ Export CSV
              </button>
            )}
          </div>
          {ptoLoading && <div className="loading">Generating report…</div>}
          {ptoRan && !ptoLoading && ptoRows.length === 0 && (
            <div className="empty">No PTO requests for this period.</div>
          )}
          {ptoRan && !ptoLoading && ptoRows.length > 0 && (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Ext ID</th>
                    <th>Type</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Reviewed By</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {ptoRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.employee?.full_name}</td>
                      <td>
                        <code>{r.employee?.external_id || "—"}</code>
                      </td>
                      <td>
                        <span
                          className={`pto-type-badge ${ptoColors[r.pto_type]}`}
                        >
                          {r.pto_type}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.start_date}</td>
                      <td style={{ fontSize: 12 }}>{r.end_date}</td>
                      <td>{r.total_hours}h</td>
                      <td>
                        <span className={`badge pto-${r.status}`}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {r.reviewer?.full_name || "—"}
                      </td>
                      <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                        {r.review_note || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── ADMIN PAGE ─────────────────────────────────────────────
function AdminPage({ profile, onProfileUpdate, onPendingChange }) {
  const today = new Date();
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [projMsg, setProjMsg] = useState("");
  const [pwModal, setPwModal] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [signoffYear, setSignoffYear] = useState(today.getFullYear());
  const [signoffMonth, setSignoffMonth] = useState(today.getMonth() + 1);
  const [signoffs, setSignoffs] = useState([]);
  const [signoffNote, setSignoffNote] = useState("");
  const [signoffMsg, setSignoffMsg] = useState("");
  const [signoffBusy, setSignoffBusy] = useState(false);
  const [settings, setSettings] = useState({ allow_period_reopen: "true" });
  const [settingsMsg, setSettingsMsg] = useState("");
  const [importMsg, setImportMsg] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newDeptName, setNewDeptName] = useState("");
  const [deptMsg, setDeptMsg] = useState("");

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    const [
      { data: allUsers },
      { data: projs },
      { data: soffs },
      { data: setts },
      { data: depts },
    ] = await Promise.all([
      supabase.from("profiles").select("*").order("full_name"),
      supabase.from("projects").select("*").order("code"),
      supabase
        .from("period_signoffs")
        .select("*, signer:signed_off_by(full_name)")
        .order("year", { ascending: false })
        .order("month", { ascending: false }),
      supabase.from("app_settings").select("*"),
      supabase.from("departments").select("*").order("name"),
    ]);
    setUsers(allUsers || []);
    setManagers(
      (allUsers || []).filter(
        (u) => u.role === "manager" && u.status === "active"
      )
    );
    setProjects(projs || []);
    setSignoffs(soffs || []);
    setDepartments(depts || []);

    const settingsObj = {};
    (setts || []).forEach((s) => {
      settingsObj[s.key] = s.value;
    });
    setSettings(settingsObj);
    setLoading(false);
  }

  async function updateSetting(key, value) {
    await supabase.from("app_settings").upsert({
      key,
      value,
      updated_by: profile.id,
      updated_at: new Date().toISOString(),
    });
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSettingsMsg("Setting saved.");
    setTimeout(() => setSettingsMsg(""), 2000);
  }

  async function signOffPeriod() {
    if (
      !window.confirm(
        `Sign off ${
          MONTH_NAMES[signoffMonth - 1]
        } ${signoffYear}? This will permanently lock ALL timecards in this period. This cannot be undone.`
      )
    )
      return;
    setSignoffBusy(true);
    setSignoffMsg("");
    const { error } = await supabase.from("period_signoffs").insert({
      year: signoffYear,
      month: signoffMonth,
      signed_off_by: profile.id,
      note: signoffNote || null,
    });
    if (error) {
      setSignoffMsg(error.message);
      setSignoffBusy(false);
      return;
    }
    setSignoffMsg(
      `${
        MONTH_NAMES[signoffMonth - 1]
      } ${signoffYear} has been signed off and locked.`
    );
    setSignoffNote("");
    setSignoffBusy(false);
    loadAll();
  }

  async function removeSignoff(signoffId) {
    if (
      !window.confirm(
        "Remove this sign-off? This will unlock the period and allow edits again."
      )
    )
      return;
    await supabase.from("period_signoffs").delete().eq("id", signoffId);
    setSignoffs((prev) => prev.filter((s) => s.id !== signoffId));
    setSignoffMsg("Sign-off removed. Period is now unlocked.");
  }

  async function approveUser(userId) {
    await supabase
      .from("profiles")
      .update({ status: "active" })
      .eq("id", userId);
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: "active" } : u))
    );
    onPendingChange();
  }
  async function bulkApproveAll() {
    const pendingIds = users
      .filter((u) => u.status === "pending")
      .map((u) => u.id);
    if (pendingIds.length === 0) return;
    await supabase
      .from("profiles")
      .update({ status: "active" })
      .in("id", pendingIds);
    setUsers((prev) =>
      prev.map((u) => (u.status === "pending" ? { ...u, status: "active" } : u))
    );
    onPendingChange();
  }
  async function deactivateUser(userId) {
    await supabase
      .from("profiles")
      .update({ status: "inactive" })
      .eq("id", userId);
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: "inactive" } : u))
    );
  }
  async function reactivateUser(userId) {
    await supabase
      .from("profiles")
      .update({ status: "active" })
      .eq("id", userId);
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: "active" } : u))
    );
  }
  async function changeRole(userId, role) {
    await supabase.from("profiles").update({ role }).eq("id", userId);
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
  }
  async function assignManager(userId, managerId) {
    await supabase
      .from("profiles")
      .update({ manager_id: managerId || null })
      .eq("id", userId);
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, manager_id: managerId || null } : u
      )
    );
  }
  async function setExternalId(userId, val) {
    await supabase
      .from("profiles")
      .update({ external_id: val })
      .eq("id", userId);
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, external_id: val } : u))
    );
  }
  async function resetPassword() {
    if (!newPw || newPw.length < 6) {
      setPwMsg("Password must be at least 6 characters.");
      return;
    }
    setPwMsg("");
    const { data, error } = await supabase.functions.invoke(
      "reset-user-password",
      { body: { userId: pwModal.userId, newPassword: newPw } }
    );
    if (error || data?.error)
      setPwMsg(error?.message || data?.error || "Failed to reset password.");
    else {
      setPwMsg("Password updated successfully!");
      setNewPw("");
      setTimeout(() => {
        setPwModal(null);
        setPwMsg("");
      }, 1500);
    }
  }
  async function handleCsvImport(file) {
    setImportBusy(true);
    setImportMsg("");
    const text = (await file.text()).replace(/^\uFEFF/, "");
    const lines = text.split("\n").filter((l) => l.trim());
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const emailIdx = headers.indexOf("email");
    const deptIdx = headers.indexOf("department");
    const extIdx = headers.indexOf("external_id");
    const mgrIdx = headers.indexOf("manager_email");

    if (emailIdx === -1) {
      setImportMsg("CSV must include an 'email' column to identify users.");
      setImportBusy(false);
      return;
    }

    let updated = 0,
      skipped = 0;
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim());
      const email = cols[emailIdx];
      if (!email) continue;

      const updates = {};
      if (deptIdx !== -1 && cols[deptIdx]) updates.department = cols[deptIdx];
      if (extIdx !== -1 && cols[extIdx]) updates.external_id = cols[extIdx];

      if (mgrIdx !== -1 && cols[mgrIdx]) {
        const { data: mgr } = await supabase
          .from("profiles")
          .select("id")
          .eq("email", cols[mgrIdx])
          .maybeSingle();
        if (mgr) updates.manager_id = mgr.id;
        else errors.push(`Row ${i + 1}: manager ${cols[mgrIdx]} not found`);
      }

      if (Object.keys(updates).length === 0) {
        skipped++;
        continue;
      }

      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("email", email);
      if (error) errors.push(`Row ${i + 1} (${email}): ${error.message}`);
      else updated++;
    }

    setImportMsg(
      `Updated ${updated}, skipped ${skipped}. ${
        errors.length ? "Errors: " + errors.join("; ") : ""
      }`
    );
    setImportBusy(false);
    loadAll();
  }
  async function addDepartment() {
    if (!newDeptName) return;
    const { error } = await supabase
      .from("departments")
      .insert({ name: newDeptName });
    if (error) {
      setDeptMsg(error.message);
      return;
    }
    setDeptMsg("Department added!");
    setNewDeptName("");
    loadAll();
    setTimeout(() => setDeptMsg(""), 2000);
  }

  async function toggleDepartment(d) {
    await supabase
      .from("departments")
      .update({ active: !d.active })
      .eq("id", d.id);
    setDepartments((prev) =>
      prev.map((dept) =>
        dept.id === d.id ? { ...dept, active: !dept.active } : dept
      )
    );
  }

  async function updateDepartment(id, value) {
    await supabase.from("departments").update({ name: value }).eq("id", id);
    setDepartments((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name: value } : d))
    );
  }
  async function addProject() {
    if (!newCode || !newName) return;
    const { error } = await supabase
      .from("projects")
      .insert({ code: newCode.toUpperCase(), name: newName });
    if (error) {
      setProjMsg(error.message);
      return;
    }
    setProjMsg("Project added!");
    setNewCode("");
    setNewName("");
    loadAll();
    setTimeout(() => setProjMsg(""), 2000);
  }
  async function toggleProject(p) {
    await supabase
      .from("projects")
      .update({ active: !p.active })
      .eq("id", p.id);
    setProjects((prev) =>
      prev.map((pr) => (pr.id === p.id ? { ...pr, active: !pr.active } : pr))
    );
  }
  async function updateProject(id, field, value) {
    await supabase
      .from("projects")
      .update({ [field]: value })
      .eq("id", id);
    setProjects((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  }

  if (loading) return <div className="loading">Loading admin…</div>;

  const pendingUsers = users.filter((u) => u.status === "pending");
  const allOtherUsers = users.filter((u) => u.id !== profile.id);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Administration</div>
        <div className="page-sub">
          Approve accounts, manage users, and maintain project codes
        </div>
      </div>
      <div className="admin-tabs">
        {[
          {
            id: "pending",
            label: `Pending Approval${
              pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ""
            }`,
          },
          { id: "users", label: "All Users" },
          { id: "projects", label: "Project Codes" },
          { id: "departments", label: "Departments" },
          { id: "signoff", label: "Period Sign-Off" },
          { id: "settings", label: "Settings" },
        ].map((t) => (
          <button
            key={t.id}
            className={`admin-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "pending" &&
        (pendingUsers.length === 0 ? (
          <div className="empty">✓ No accounts awaiting approval.</div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <span style={{ fontSize: 13, color: "var(--gray-600)" }}>
                {pendingUsers.length} account
                {pendingUsers.length !== 1 ? "s" : ""} waiting for approval
              </span>
              <button className="btn btn-success" onClick={bulkApproveAll}>
                ✓ Approve All ({pendingUsers.length})
              </button>
            </div>
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Requested Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.full_name}</td>
                      <td>{u.email}</td>
                      <td>
                        <span className="badge badge-pending">{u.role}</span>
                      </td>
                      <td>
                        <div className="action-cell">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => approveUser(u.id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deactivateUser(u.id)}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {tab === "users" && (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <label
              className="btn btn-primary"
              style={{ cursor: "pointer", margin: 0 }}
            >
              {importBusy ? "Importing…" : "📥 Import CSV"}

              <input
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                disabled={importBusy}
                onChange={(e) =>
                  e.target.files[0] && handleCsvImport(e.target.files[0])
                }
              />
            </label>
            <span style={{ fontSize: 11, color: "var(--gray-500)" }}>
              CSV columns: email (required), department, external_id,
              manager_email
            </span>
          </div>
          {importMsg && (
            <div className={importMsg.includes("Error") ? "error" : "success"}>
              {importMsg}
            </div>
          )}
          <div className="tbl-wrap admin-compact">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Worker Type</th>
                  <th>PIN / Badge</th>
                  <th>Ext. ID</th>
                  <th>Department</th>
                  <th>Manager</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allOtherUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.full_name}</td>
                    <td style={{ fontSize: 12, color: "var(--muted)" }}>
                      {u.email}
                    </td>
                    <td>
                      <select
                        className="inline"
                        value={u.role}
                        onChange={(e) => changeRole(u.id, e.target.value)}
                      >
                        <option value="employee">Employee</option>
                        <option value="manager">Manager</option>
                        <option value="administrator">Administrator</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="inline"
                        value={u.worker_type || "salaried"}
                        onChange={async (e) => {
                          await supabase
                            .from("profiles")
                            .update({ worker_type: e.target.value })
                            .eq("id", u.id);
                          setUsers((prev) =>
                            prev.map((x) =>
                              x.id === u.id
                                ? { ...x, worker_type: e.target.value }
                                : x
                            )
                          );
                        }}
                      >
                        <option value="salaried">Salaried</option>
                        <option value="hourly">Hourly</option>
                      </select>
                    </td>
                    <td>
                      {u.worker_type === "hourly" ? (
                        <EditableCell
                          value={u.kiosk_pin || u.badge_id || ""}
                          onSave={async (val) => {
                            const field =
                              settings.kiosk_auth_mode === "pin"
                                ? "kiosk_pin"
                                : "badge_id";
                            await supabase
                              .from("profiles")
                              .update({ [field]: val })
                              .eq("id", u.id);
                            setUsers((prev) =>
                              prev.map((x) =>
                                x.id === u.id ? { ...x, [field]: val } : x
                              )
                            );
                          }}
                          placeholder={
                            settings.kiosk_auth_mode === "pin"
                              ? "Set PIN…"
                              : "Set Badge ID…"
                          }
                        />
                      ) : (
                        <span
                          style={{ color: "var(--gray-400)", fontSize: 12 }}
                        >
                          N/A
                        </span>
                      )}
                    </td>
                    <td>
                      <EditableCell
                        value={u.external_id || ""}
                        onSave={(val) => setExternalId(u.id, val)}
                        placeholder="Set ID…"
                      />
                    </td>
                    <td>
                      <select
                        className="inline"
                        value={u.department || ""}
                        onChange={async (e) => {
                          const newVal = e.target.value;
                          const { error } = await supabase
                            .from("profiles")
                            .update({ department: newVal || null })
                            .eq("id", u.id);
                          if (!error) {
                            setUsers((prev) =>
                              prev.map((x) =>
                                x.id === u.id ? { ...x, department: newVal } : x
                              )
                            );
                          }
                        }}
                      >
                        <option value="">— None —</option>
                        {departments
                          .filter((d) => d.active)
                          .map((d) => (
                            <option key={d.id} value={d.name}>
                              {d.name}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="inline"
                        value={u.manager_id || ""}
                        onChange={(e) =>
                          assignManager(u.id, e.target.value || null)
                        }
                      >
                        <option value="">— None —</option>
                        {users
                          .filter(
                            (m) =>
                              m.status === "active" &&
                              (m.role === "manager" ||
                                m.role === "administrator") &&
                              (m.id !== u.id || u.role === "administrator")
                          )
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.full_name} ({m.role})
                              {m.id === profile.id ? " — you" : ""}
                            </option>
                          ))}
                      </select>
                    </td>

                    <td>
                      <span className={`badge badge-${u.status}`}>
                        {u.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        {u.status === "active" && (
                          <>
                            <button
                              className="btn btn-sm btn-purple"
                              onClick={() => {
                                setPwModal({ userId: u.id, name: u.full_name });
                                setPwMsg("");
                                setNewPw("");
                              }}
                            >
                              Reset PW
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deactivateUser(u.id)}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                        {u.status === "inactive" && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => reactivateUser(u.id)}
                          >
                            Reactivate
                          </button>
                        )}
                        {u.status === "pending" && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => approveUser(u.id)}
                          >
                            Approve
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "projects" && (
        <div>
          {projMsg && <div className="success">{projMsg}</div>}
          <div className="card">
            <div className="card-title">Add New Project Code</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                placeholder="CODE"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                style={{
                  width: 110,
                  padding: "8px",
                  border: "1px solid var(--border)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                }}
              />
              <input
                placeholder="Project Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px",
                  border: "1px solid var(--border)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                }}
              />
              <button className="btn btn-success" onClick={addProject}>
                Add
              </button>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <EditableCell
                        value={p.code}
                        onSave={(val) =>
                          updateProject(p.id, "code", val.toUpperCase())
                        }
                        placeholder="Code"
                      />
                    </td>
                    <td>
                      <EditableCell
                        value={p.name}
                        onSave={(val) => updateProject(p.id, "name", val)}
                        placeholder="Name"
                      />
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          p.active ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${
                          p.active ? "btn-danger" : "btn-success"
                        }`}
                        onClick={() => toggleProject(p)}
                      >
                        {p.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tab === "departments" && (
        <div>
          {deptMsg && <div className="success">{deptMsg}</div>}
          <div className="card">
            <div className="card-title">Add New Department</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                placeholder="Department Name"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px",
                  border: "1px solid var(--border)",
                  fontSize: 13,
                }}
              />
              <button className="btn btn-success" onClick={addDepartment}>
                Add
              </button>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <EditableCell
                        value={d.name}
                        onSave={(val) => updateDepartment(d.id, val)}
                        placeholder="Name"
                      />
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          d.active ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {d.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${
                          d.active ? "btn-danger" : "btn-success"
                        }`}
                        onClick={() => toggleDepartment(d)}
                      >
                        {d.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "signoff" && (
        <div>
          {signoffMsg && (
            <div
              className={
                signoffMsg.includes("locked") || signoffMsg.includes("removed")
                  ? "success"
                  : "error"
              }
            >
              {signoffMsg}
            </div>
          )}
          <div className="card">
            <div className="card-title">Sign Off a Period</div>
            <p
              style={{
                fontSize: 12,
                color: "var(--gray-600)",
                marginBottom: 16,
                lineHeight: 1.6,
              }}
            >
              Signing off a period permanently locks{" "}
              <strong>all timecards</strong> in that month for all employees. No
              edits, approvals, or unapprovals will be possible. This is
              intended for month-end close. You will be asked to confirm before
              proceeding.
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Year</label>
                <select
                  className="inline"
                  value={signoffYear}
                  onChange={(e) => setSignoffYear(+e.target.value)}
                >
                  {[
                    today.getFullYear() - 1,
                    today.getFullYear(),
                    today.getFullYear() + 1,
                  ].map((y) => (
                    <option key={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Month</label>
                <select
                  className="inline"
                  value={signoffMonth}
                  onChange={(e) => setSignoffMonth(+e.target.value)}
                >
                  {MONTH_NAMES.map((n, i) => (
                    <option key={i} value={i + 1}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className="field"
                style={{ marginBottom: 0, flex: 1, minWidth: 200 }}
              >
                <label>Note (optional)</label>
                <input
                  value={signoffNote}
                  onChange={(e) => setSignoffNote(e.target.value)}
                  placeholder="e.g. Month-end close Q1 2026"
                  style={{
                    padding: "6px 10px",
                    border: "1px solid var(--border)",
                    fontFamily: "var(--font)",
                    fontSize: 13,
                    width: "100%",
                  }}
                />
              </div>
              <button
                className="btn btn-danger"
                disabled={
                  signoffBusy ||
                  signoffs.some(
                    (s) => s.year === signoffYear && s.month === signoffMonth
                  )
                }
                onClick={signOffPeriod}
              >
                {signoffs.some(
                  (s) => s.year === signoffYear && s.month === signoffMonth
                )
                  ? "Already Signed Off"
                  : "🔒 Sign Off Period"}
              </button>
            </div>
          </div>
          <div className="card-title" style={{ marginBottom: 12 }}>
            Sign-Off History
          </div>
          {signoffs.length === 0 ? (
            <div className="empty">No periods have been signed off yet.</div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Signed Off By</th>
                    <th>Date</th>
                    <th>Note</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {signoffs.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>
                          {MONTH_NAMES[s.month - 1]} {s.year}
                        </strong>
                      </td>
                      <td>{s.signer?.full_name || "—"}</td>
                      <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                        {fmtDateTime(s.signed_off_at)}
                      </td>
                      <td style={{ fontSize: 12, color: "var(--gray-600)" }}>
                        {s.note || "—"}
                      </td>
                      <td>
                        {settings.allow_period_reopen === "true" ? (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => removeSignoff(s.id)}
                          >
                            Unlock
                          </button>
                        ) : (
                          <span
                            style={{ fontSize: 12, color: "var(--gray-400)" }}
                          >
                            🔒 Reopening disabled
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div>
          {settingsMsg && <div className="success">{settingsMsg}</div>}
          <div className="card">
            <div className="card-title">Application Settings</div>
            <p
              style={{
                fontSize: 12,
                color: "var(--gray-500)",
                marginBottom: 20,
              }}
            >
              These settings control system-wide behavior for all users.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "16px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--gray-900)",
                    marginBottom: 4,
                  }}
                >
                  Allow Period Reopening
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--gray-500)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                  }}
                >
                  When enabled, administrators can unlock signed-off periods to
                  allow corrections. When disabled, period sign-offs are
                  permanent and the Unlock button is hidden.
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginLeft: 24,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
                  {settings.allow_period_reopen === "true"
                    ? "Enabled"
                    : "Disabled"}
                </span>
                <div
                  onClick={() =>
                    updateSetting(
                      "allow_period_reopen",
                      settings.allow_period_reopen === "true" ? "false" : "true"
                    )
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    cursor: "pointer",
                    background:
                      settings.allow_period_reopen === "true"
                        ? "var(--green)"
                        : "var(--gray-300)",
                    position: "relative",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      left: settings.allow_period_reopen === "true" ? 23 : 3,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "white",
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "16px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--gray-900)",
                  marginBottom: 4,
                }}
              >
                Kiosk Authentication Mode
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--gray-500)",
                  maxWidth: 500,
                  lineHeight: 1.6,
                  marginBottom: 12,
                }}
              >
                How hourly workers authenticate at the iPad kiosk. PIN uses a
                numeric code set per employee. Badge uses their external
                badge/ID number.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {["pin", "badge"].map((mode) => (
                  <button
                    key={mode}
                    className={`btn btn-sm ${
                      settings.kiosk_auth_mode === mode
                        ? "btn-primary"
                        : "btn-outline"
                    }`}
                    onClick={() => updateSetting("kiosk_auth_mode", mode)}
                  >
                    {mode === "pin" ? "🔢 PIN Code" : "🪪 Badge / ID Number"}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "16px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--gray-900)",
                    marginBottom: 4,
                  }}
                >
                  Require Break Attestation
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--gray-500)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                  }}
                >
                  When enabled, hourly workers must confirm they took their
                  required breaks before clocking out.
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginLeft: 24,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
                  {settings.kiosk_break_required === "true"
                    ? "Required"
                    : "Optional"}
                </span>
                <div
                  onClick={() =>
                    updateSetting(
                      "kiosk_break_required",
                      settings.kiosk_break_required === "true"
                        ? "false"
                        : "true"
                    )
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    cursor: "pointer",
                    background:
                      settings.kiosk_break_required === "true"
                        ? "var(--green)"
                        : "var(--gray-300)",
                    position: "relative",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      left: settings.kiosk_break_required === "true" ? 23 : 3,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "white",
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-title">Kiosk Mode</div>
            <p
              style={{
                fontSize: 12,
                color: "var(--gray-500)",
                marginBottom: 12,
                lineHeight: 1.6,
              }}
            >
              Share this link with your iPad kiosk for hourly workers to clock
              in/out. Open it in fullscreen (F11) for the best experience.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  background: "var(--gray-100)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                {window.location.origin}/?kiosk=1
              </code>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(
                    window.location.origin + "/?kiosk=1"
                  );
                  setSettingsMsg("Kiosk URL copied!");
                }}
              >
                Copy
              </button>
            </div>
          </div>
          <HolidaysSettings />
        </div>
      )}

      {pwModal && (
        <div
          className="overlay"
          onClick={(e) => e.target === e.currentTarget && setPwModal(null)}
        >
          <div className="modal">
            <div className="modal-title">Reset Password</div>
            <p
              style={{
                fontSize: 13,
                color: "var(--gray-600)",
                marginBottom: 16,
              }}
            >
              Setting new password for <strong>{pwModal.name}</strong>
            </p>
            {pwMsg && (
              <div className={pwMsg.includes("success") ? "success" : "error"}>
                {pwMsg}
              </div>
            )}
            <div className="field">
              <label>New Password</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Min 6 characters"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => setPwModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-purple" onClick={resetPassword}>
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── THINK TANK PAGE ────────────────────────────────────────
function ThinkTankPage({ profile }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [commentBusy, setCommentBusy] = useState({});

  useEffect(() => {
    loadPosts();
  }, []);

  async function loadPosts() {
    setLoading(true);
    const { data } = await supabase
      .from("thinktank_posts")
      .select(
        `id, content, created_at, author:author_id(id, full_name, role), likes:thinktank_likes(id, user_id), comments:thinktank_comments(id, content, created_at, author:author_id(id, full_name, role))`
      )
      .order("created_at", { ascending: false });
    setPosts(data || []);
    setLoading(false);
  }

  function initials(name) {
    return (name || "?")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  async function submitPost() {
    if (!newPost.trim()) return;
    setPosting(true);
    const { data, error } = await supabase
      .from("thinktank_posts")
      .insert({ author_id: profile.id, content: newPost.trim() })
      .select(`id, content, created_at, author:author_id(id, full_name, role)`)
      .single();
    if (!error && data) {
      setPosts((prev) => [{ ...data, likes: [], comments: [] }, ...prev]);
      setNewPost("");
    }
    setPosting(false);
  }

  async function toggleLike(post) {
    const myLike = post.likes?.find((l) => l.user_id === profile.id);
    if (myLike) {
      await supabase.from("thinktank_likes").delete().eq("id", myLike.id);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, likes: p.likes.filter((l) => l.id !== myLike.id) }
            : p
        )
      );
    } else {
      const { data } = await supabase
        .from("thinktank_likes")
        .insert({ post_id: post.id, user_id: profile.id })
        .select()
        .single();
      if (data)
        setPosts((prev) =>
          prev.map((p) =>
            p.id === post.id ? { ...p, likes: [...p.likes, data] } : p
          )
        );
    }
  }

  async function submitComment(post) {
    const text = (commentInputs[post.id] || "").trim();
    if (!text) return;
    setCommentBusy((prev) => ({ ...prev, [post.id]: true }));
    const { data } = await supabase
      .from("thinktank_comments")
      .insert({ post_id: post.id, author_id: profile.id, content: text })
      .select(`id, content, created_at, author:author_id(id, full_name, role)`)
      .single();
    if (data) {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id ? { ...p, comments: [...p.comments, data] } : p
        )
      );
      setCommentInputs((prev) => ({ ...prev, [post.id]: "" }));
    }
    setCommentBusy((prev) => ({ ...prev, [post.id]: false }));
  }

  async function deletePost(postId) {
    if (!window.confirm("Delete this post?")) return;
    await supabase.from("thinktank_posts").delete().eq("id", postId);
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  }

  async function deleteComment(postId, commentId) {
    await supabase.from("thinktank_comments").delete().eq("id", commentId);
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, comments: p.comments.filter((c) => c.id !== commentId) }
          : p
      )
    );
  }

  function toggleComments(postId) {
    setExpandedComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  }

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return fmtDate(new Date(ts));
  }

  const roleColor = {
    employee: "var(--gray-500)",
    manager: "var(--sage)",
    administrator: "var(--purple)",
  };

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div className="page-header">
        <div className="page-title">Think Tank</div>
        <div className="page-sub">
          Share ideas, updates, and thoughts with the team
        </div>
      </div>
      <div className="tt-composer">
        <textarea
          value={newPost}
          onChange={(e) => setNewPost(e.target.value)}
          placeholder="Share something with the team…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.metaKey) submitPost();
          }}
        />
        <div className="tt-composer-actions">
          <button
            className="btn btn-primary"
            onClick={submitPost}
            disabled={posting || !newPost.trim()}
          >
            {posting ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
      {loading && <div className="loading">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div className="tt-empty">
          No posts yet — be the first to share something!
        </div>
      )}
      {posts.map((post) => {
        const myLike = post.likes?.find((l) => l.user_id === profile.id);
        const likeCount = post.likes?.length || 0;
        const commentCount = post.comments?.length || 0;
        const showComments = expandedComments[post.id];
        const canDelete =
          post.author?.id === profile.id || profile.role === "administrator";
        return (
          <div key={post.id} className="tt-post">
            <div className="tt-post-header">
              <div
                className="tt-avatar"
                style={{
                  background: roleColor[post.author?.role] || "var(--blue)",
                }}
              >
                {initials(post.author?.full_name)}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="tt-author">{post.author?.full_name}</span>
                  <span
                    className="tt-role-badge"
                    style={{
                      background: `${roleColor[post.author?.role]}22`,
                      color: roleColor[post.author?.role],
                    }}
                  >
                    {post.author?.role}
                  </span>
                </div>
              </div>
              <span className="tt-time">{timeAgo(post.created_at)}</span>
            </div>
            <div className="tt-content">{post.content}</div>
            <div className="tt-actions">
              <button
                className={`tt-action-btn ${myLike ? "liked" : ""}`}
                onClick={() => toggleLike(post)}
              >
                {myLike ? "❤️" : "🤍"} {likeCount > 0 && likeCount}
              </button>
              <button
                className="tt-action-btn"
                onClick={() => toggleComments(post.id)}
              >
                💬{" "}
                {commentCount > 0
                  ? `${commentCount} comment${commentCount !== 1 ? "s" : ""}`
                  : "Comment"}
              </button>
              {canDelete && (
                <button
                  className="tt-action-btn tt-delete-btn"
                  onClick={() => deletePost(post.id)}
                >
                  🗑
                </button>
              )}
            </div>
            {showComments && (
              <div className="tt-comments">
                {post.comments?.map((c) => (
                  <div key={c.id} className="tt-comment">
                    <div
                      className="tt-comment-avatar"
                      style={{
                        background:
                          roleColor[c.author?.role] || "var(--gray-300)",
                      }}
                    >
                      {initials(c.author?.full_name)}
                    </div>
                    <div className="tt-comment-body">
                      <span className="tt-comment-author">
                        {c.author?.full_name}
                      </span>
                      <span className="tt-comment-time">
                        {timeAgo(c.created_at)}
                      </span>
                      {(c.author?.id === profile.id ||
                        profile.role === "administrator") && (
                        <button
                          style={{
                            marginLeft: 6,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--gray-400)",
                            fontSize: 11,
                          }}
                          onClick={() => deleteComment(post.id, c.id)}
                        >
                          ✕
                        </button>
                      )}
                      <div className="tt-comment-text">{c.content}</div>
                    </div>
                  </div>
                ))}
                <div className="tt-comment-input">
                  <div
                    className="tt-comment-avatar"
                    style={{
                      background: roleColor[profile.role] || "var(--blue)",
                    }}
                  >
                    {initials(profile.full_name)}
                  </div>
                  <input
                    placeholder="Write a comment…"
                    value={commentInputs[post.id] || ""}
                    onChange={(e) =>
                      setCommentInputs((prev) => ({
                        ...prev,
                        [post.id]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitComment(post);
                    }}
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={
                      commentBusy[post.id] ||
                      !(commentInputs[post.id] || "").trim()
                    }
                    onClick={() => submitComment(post)}
                  >
                    {commentBusy[post.id] ? "…" : "Send"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── KIOSK PAGE ─────────────────────────────────────────────
function KioskPage() {
  const [settings, setSettings] = useState({
    kiosk_auth_mode: "pin",
    kiosk_break_required: "true",
  });
  const [step, setStep] = useState("identify");
  const [pin, setPin] = useState("");
  const [employee, setEmployee] = useState(null);
  const [activePunch, setActivePunch] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [attestation, setAttestation] = useState({
    breaksTaken: false,
    injuryFree: false,
  });
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    const { data } = await supabase.from("app_settings").select("*");
    const obj = {};
    (data || []).forEach((s) => {
      obj[s.key] = s.value;
    });
    setSettings((s) => ({ ...s, ...obj }));
  }

  async function identify() {
    if (!pin) return;
    setErr("");
    setBusy(true);
    const field = settings.kiosk_auth_mode === "pin" ? "kiosk_pin" : "badge_id";
    const { data: emp } = await supabase
      .from("profiles")
      .select("*")
      .eq(field, pin)
      .eq("status", "active")
      .eq("worker_type", "hourly")
      .maybeSingle();
    if (!emp) {
      setErr("Employee not found. Please try again.");
      setPin("");
      setBusy(false);
      return;
    }
    setEmployee(emp);
    const { data: punch } = await supabase
      .from("punches")
      .select("*")
      .eq("employee_id", emp.id)
      .is("punch_out", null)
      .maybeSingle();
    setActivePunch(punch || null);
    setStep("action");
    setBusy(false);
  }

  async function punchIn() {
    setBusy(true);
    const { data } = await supabase
      .from("punches")
      .insert({ employee_id: employee.id, punch_in: new Date().toISOString() })
      .select()
      .single();
    setActivePunch(data);
    setBusy(false);
  }

  async function startBreak() {
    setBusy(true);
    await supabase
      .from("punches")
      .update({ break_start: new Date().toISOString() })
      .eq("id", activePunch.id);
    setActivePunch((p) => ({ ...p, break_start: new Date().toISOString() }));
    setBusy(false);
  }

  async function endBreak() {
    setBusy(true);
    await supabase
      .from("punches")
      .update({ break_end: new Date().toISOString(), break_taken: true })
      .eq("id", activePunch.id);
    setActivePunch((p) => ({
      ...p,
      break_end: new Date().toISOString(),
      break_taken: true,
    }));
    setBusy(false);
  }

  async function initiatePunchOut() {
    setStep("attestation");
  }

  async function completePunchOut() {
    setBusy(true);
    const now = new Date();
    await supabase
      .from("punches")
      .update({
        punch_out: now.toISOString(),
        break_taken: attestation.breaksTaken,
        injury_free: attestation.injuryFree,
        attested_at: now.toISOString(),
        attestation_text: `Break taken: ${attestation.breaksTaken}. No injuries: ${attestation.injuryFree}.`,
      })
      .eq("id", activePunch.id);
    setStep("done");
    setBusy(false);
  }

  function reset() {
    setStep("identify");
    setPin("");
    setEmployee(null);
    setActivePunch(null);
    setErr("");
    setAttestation({ breaksTaken: false, injuryFree: false });
  }
  function addPin(d) {
    if (pin.length < 6) setPin((p) => p + d);
  }
  function delPin() {
    setPin((p) => p.slice(0, -1));
  }

  const timeStr = clock.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
  const dateStr = clock.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const punchInTime = activePunch?.punch_in
    ? new Date(activePunch.punch_in).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
  const onBreak = activePunch?.break_start && !activePunch?.break_end;

  return (
    <div className="kiosk-wrap">
      <div className="kiosk-card">
        <div className="kiosk-logo">⏱ Time With Joe!</div>
        <div className="kiosk-sub">Employee Time Kiosk</div>
        {step === "identify" && (
          <>
            <div className="kiosk-time">{timeStr}</div>
            <div className="kiosk-date">{dateStr}</div>
            {err && <div className="error">{err}</div>}
            <p
              style={{
                fontSize: 13,
                color: "var(--gray-500)",
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              Enter your{" "}
              {settings.kiosk_auth_mode === "pin" ? "PIN" : "Badge / ID Number"}
              :
            </p>
            <div className="kiosk-pin-display">
              {[...Array(Math.max(pin.length, 4))].map((_, i) => (
                <div
                  key={i}
                  className={`kiosk-pin-dot ${i < pin.length ? "filled" : ""}`}
                />
              ))}
            </div>
            <div className="kiosk-numpad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, "⌫", 0, "✓"].map((k, i) => (
                <button
                  key={i}
                  className={`kiosk-key ${k === "⌫" ? "danger" : ""}`}
                  onClick={() => {
                    if (k === "⌫") delPin();
                    else if (k === "✓") identify();
                    else addPin(String(k));
                  }}
                >
                  {k}
                </button>
              ))}
            </div>
          </>
        )}
        {step === "action" && employee && (
          <>
            <div className="kiosk-name">👋 {employee.full_name}</div>
            <div className="kiosk-time">{timeStr}</div>
            {activePunch && (
              <div className="kiosk-punched-in">
                ✓ Clocked in at {punchInTime}
                {onBreak ? " — On Break" : ""}
              </div>
            )}
            <div className="kiosk-status">
              {!activePunch && (
                <button
                  className="kiosk-action-btn punch-in"
                  onClick={punchIn}
                  disabled={busy}
                >
                  🟢 Clock In
                </button>
              )}
              {activePunch && !onBreak && !activePunch.break_taken && (
                <button
                  className="kiosk-action-btn break-start"
                  onClick={startBreak}
                  disabled={busy}
                >
                  ☕ Start Break
                </button>
              )}
              {activePunch && onBreak && (
                <button
                  className="kiosk-action-btn break-end"
                  onClick={endBreak}
                  disabled={busy}
                >
                  ▶ End Break
                </button>
              )}
              {activePunch && !onBreak && (
                <button
                  className="kiosk-action-btn punch-out"
                  onClick={initiatePunchOut}
                  disabled={busy}
                >
                  🔴 Clock Out
                </button>
              )}
              <button
                className="btn btn-outline"
                onClick={reset}
                style={{ marginTop: 8 }}
              >
                ← Back
              </button>
            </div>
          </>
        )}
        {step === "attestation" && (
          <>
            <div className="kiosk-name">{employee?.full_name}</div>
            <div
              style={{
                fontSize: 13,
                color: "var(--gray-500)",
                textAlign: "center",
                margin: "8px 0 20px",
              }}
            >
              Before you clock out, please confirm:
            </div>
            <div className="kiosk-attestation">
              <h3>End of Shift Confirmation</h3>
              <label className="kiosk-check">
                <input
                  type="checkbox"
                  checked={attestation.breaksTaken}
                  onChange={(e) =>
                    setAttestation((a) => ({
                      ...a,
                      breaksTaken: e.target.checked,
                    }))
                  }
                />
                <span>
                  I confirm I took my required meal and rest breaks during this
                  shift.
                </span>
              </label>
              <label className="kiosk-check">
                <input
                  type="checkbox"
                  checked={attestation.injuryFree}
                  onChange={(e) =>
                    setAttestation((a) => ({
                      ...a,
                      injuryFree: e.target.checked,
                    }))
                  }
                />
                <span>
                  I confirm I was not injured during this shift and have no
                  injuries to report.
                </span>
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button
                className="btn btn-outline"
                onClick={() => setStep("action")}
                style={{ flex: 1 }}
              >
                ← Back
              </button>
              <button
                className="kiosk-action-btn punch-out"
                onClick={completePunchOut}
                disabled={
                  busy || !attestation.breaksTaken || !attestation.injuryFree
                }
                style={{ flex: 2 }}
              >
                {busy ? "Processing…" : "✓ Clock Out"}
              </button>
            </div>
          </>
        )}
        {step === "done" && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div className="kiosk-name">{employee?.full_name}</div>
            <div
              style={{
                fontSize: 16,
                color: "var(--green)",
                fontWeight: 600,
                margin: "8px 0",
              }}
            >
              Clocked Out Successfully
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--gray-500)",
                marginBottom: 24,
              }}
            >
              Have a great rest of your day!
            </div>
            <button className="kiosk-action-btn punch-in" onClick={reset}>
              Next Employee →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PTO PAGE ───────────────────────────────────────────────
function PTOPage({ profile }) {
  const today = new Date();
  const [tab, setTab] = useState(
    profile.role === "manager" || profile.role === "administrator"
      ? "approvals"
      : "mine"
  );
  const [myRequests, setMyRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    pto_type: "vacation",
    start_date: "",
    end_date: "",
    hours_per_day: "8",
    reason: "",
  });
  const [formErr, setFormErr] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState({});

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    const [myRes, pendRes] = await Promise.all([
      supabase
        .from("pto_requests")
        .select("*, reviewer:reviewed_by(full_name)")
        .eq("employee_id", profile.id)
        .order("created_at", { ascending: false }),
      profile.role === "manager" || profile.role === "administrator"
        ? supabase
            .from("pto_requests")
            .select(
              "*, employee:employee_id(full_name,external_id,manager_id), reviewer:reviewed_by(full_name)"
            )
            .eq("status", "pending")
            .eq("manager_id", profile.id)
            .order("created_at", { ascending: false })
        : { data: [] },
    ]);
    setMyRequests(myRes.data || []);
    console.log("pendRes:", pendRes);
    setPendingRequests(pendRes.data || []);
    setPendingRequests(pendRes.data || []);
    setLoading(false);
  }

  function calcTotalHours() {
    if (!form.start_date || !form.end_date) return 0;
    const [sy, sm, sd] = form.start_date.split("-").map(Number);
    const [ey, em, ed] = form.end_date.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    let days = 0,
      cur = new Date(start);
    while (cur <= end) {
      const d = cur.getDay();
      if (d !== 0 && d !== 6) days++;
      cur.setDate(cur.getDate() + 1);
    }
    return days * parseFloat(form.hours_per_day || 8);
  }

  async function submitRequest() {
    if (!form.start_date || !form.end_date) {
      setFormErr("Please select start and end dates.");
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setFormErr("End date must be after start date.");
      return;
    }
    setFormBusy(true);
    setFormErr("");
    const total = calcTotalHours();
    const { data: freshProfile } = await supabase
      .from("profiles")
      .select("manager_id")
      .eq("id", profile.id)
      .single();
    const { error } = await supabase.from("pto_requests").insert({
      employee_id: profile.id,
      manager_id: freshProfile?.manager_id || profile.manager_id,
      pto_type: form.pto_type,
      start_date: form.start_date,
      end_date: form.end_date,
      hours_per_day: parseFloat(form.hours_per_day),
      total_hours: total,
      reason: form.reason || null,
      status: "pending",
    });
    if (error) {
      setFormErr(error.message);
      setFormBusy(false);
      return;
    }
    setForm({
      pto_type: "vacation",
      start_date: "",
      end_date: "",
      hours_per_day: "8",
      reason: "",
    });
    setShowForm(false);
    setFormBusy(false);
    loadAll();
  }

  async function cancelRequest(reqId) {
    if (!window.confirm("Cancel this PTO request?")) return;
    await supabase
      .from("pto_requests")
      .update({ status: "denied", review_note: "Cancelled by employee" })
      .eq("id", reqId);
    loadAll();
  }

  async function reviewRequest(req, status) {
    const note = reviewNote[req.id] || "";
    const { error: updateErr } = await supabase
      .from("pto_requests")
      .update({
        status,
        reviewed_by: profile.id,
        reviewed_at: new Date().toISOString(),
        review_note: note || null,
      })
      .eq("id", req.id);
    console.log(
      "reviewRequest updateErr:",
      updateErr,
      "status:",
      status,
      "req.id:",
      req.id
    );
    if (status === "approved") {
      function parseDateLocal(str) {
        const [y, m, d] = str.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      const start = parseDateLocal(req.start_date),
        end = parseDateLocal(req.end_date);
      end.setHours(23, 59, 59); // ensure end date is fully inclusive
      let cur = new Date(start);
      const entriesByMonth = {};
      while (cur <= end) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) {
          const yr = cur.getFullYear(),
            mo = cur.getMonth() + 1;
          const key = `${yr}-${mo}`;
          if (!entriesByMonth[key])
            entriesByMonth[key] = { year: yr, month: mo, dates: [] };
          const ds = `${yr}-${String(mo).padStart(2, "0")}-${String(
            cur.getDate()
          ).padStart(2, "0")}`;
          entriesByMonth[key].dates.push(ds);
        }
        cur.setDate(cur.getDate() + 1);
      }
      const { data: ptoProj } = await supabase
        .from("projects")
        .select("id")
        .eq("code", "PTO")
        .maybeSingle();
      if (ptoProj) {
        for (const [, v] of Object.entries(entriesByMonth)) {
          let { data: tc } = await supabase
            .from("timecards")
            .select("id,status")
            .eq("employee_id", req.employee_id)
            .eq("year", v.year)
            .eq("month", v.month)
            .maybeSingle();
          if (!tc) {
            const { data: newTc } = await supabase
              .from("timecards")
              .insert({
                employee_id: req.employee_id,
                year: v.year,
                month: v.month,
                status: "unapproved",
              })
              .select()
              .single();
            tc = newTc;
          }
          if (tc && tc.status !== "approved") {
            for (const date of v.dates) {
              await supabase.from("timecard_entries").insert({
                timecard_id: tc.id,
                entry_date: date,
                hours: req.hours_per_day,
                project_id: ptoProj.id,
              });
            }
          }
        }
      }
    }
    loadAll();
  }

  const ptoColors = {
    vacation: "pto-vacation",
    sick: "pto-sick",
    bereavement: "pto-bereavement",
    unpaid: "pto-unpaid",
  };

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div className="page-title">PTO Requests</div>
          <div className="page-sub">Request and manage time off</div>
        </div>
        {!showForm && tab === "mine" && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Request
          </button>
        )}
      </div>
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">New PTO Request</div>
          {formErr && <div className="error">{formErr}</div>}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div className="field">
              <label>Type</label>
              <select
                value={form.pto_type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, pto_type: e.target.value }))
                }
              >
                <option value="vacation">Vacation</option>
                <option value="sick">Sick</option>
                <option value="bereavement">Bereavement</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div className="field">
              <label>Hours Per Day</label>
              <select
                value={form.hours_per_day}
                onChange={(e) =>
                  setForm((f) => ({ ...f, hours_per_day: e.target.value }))
                }
              >
                {HOURS_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}h
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, start_date: e.target.value }))
                }
              />
            </div>
            <div className="field">
              <label>End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, end_date: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="field">
            <label>Reason (optional)</label>
            <input
              value={form.reason}
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
              placeholder="Brief description…"
            />
          </div>
          {form.start_date && form.end_date && (
            <div className="info" style={{ marginBottom: 12 }}>
              Total: <strong>{calcTotalHours()}h</strong> (
              {calcTotalHours() / parseFloat(form.hours_per_day)} weekdays)
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-outline"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={submitRequest}
              disabled={formBusy}
            >
              {formBusy ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </div>
      )}
      {(profile.role === "manager" || profile.role === "administrator") && (
        <div className="admin-tabs" style={{ marginBottom: 16 }}>
          <button
            className={`admin-tab ${tab === "mine" ? "active" : ""}`}
            onClick={() => setTab("mine")}
          >
            My Requests
          </button>
          <button
            className={`admin-tab ${tab === "approvals" ? "active" : ""}`}
            onClick={() => setTab("approvals")}
          >
            Pending Approvals
            {pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ""}
          </button>
        </div>
      )}
      {loading && <div className="loading">Loading…</div>}
      {!loading &&
        tab === "mine" &&
        (myRequests.length === 0 ? (
          <div className="empty">No PTO requests yet.</div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Hours</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Reviewed By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {myRequests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span
                        className={`pto-type-badge ${ptoColors[r.pto_type]}`}
                      >
                        {r.pto_type}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.start_date === r.end_date
                        ? r.start_date
                        : `${r.start_date} → ${r.end_date}`}
                    </td>
                    <td>{r.total_hours}h</td>
                    <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                      {r.reason || "—"}
                    </td>
                    <td>
                      <span className={`badge pto-${r.status}`}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                      {r.reviewer?.full_name || "—"}
                    </td>
                    <td>
                      {r.status === "pending" && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => cancelRequest(r.id)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {!loading &&
        tab === "approvals" &&
        (pendingRequests.length === 0 ? (
          <div className="empty">No pending PTO requests.</div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Hours</th>
                  <th>Reason</th>
                  <th>Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.employee?.full_name}
                      <br />
                      <span style={{ fontSize: 11, color: "var(--gray-400)" }}>
                        {r.employee?.external_id || ""}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pto-type-badge ${ptoColors[r.pto_type]}`}
                      >
                        {r.pto_type}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.start_date === r.end_date
                        ? r.start_date
                        : `${r.start_date} → ${r.end_date}`}
                    </td>
                    <td>{r.total_hours}h</td>
                    <td style={{ fontSize: 12, color: "var(--gray-500)" }}>
                      {r.reason || "—"}
                    </td>
                    <td>
                      <input
                        placeholder="Optional note…"
                        value={reviewNote[r.id] || ""}
                        onChange={(e) =>
                          setReviewNote((n) => ({
                            ...n,
                            [r.id]: e.target.value,
                          }))
                        }
                        style={{
                          padding: "4px 8px",
                          border: "1px solid var(--border)",
                          borderRadius: 2,
                          fontSize: 12,
                          width: 140,
                        }}
                      />
                    </td>
                    <td>
                      <div className="action-cell">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => reviewRequest(r, "approved")}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => reviewRequest(r, "denied")}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}

// ── HOLIDAYS SETTINGS ──────────────────────────────────────
function HolidaysSettings() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: "", name: "", hours: "8" });
  const [msg, setMsg] = useState("");

  useEffect(() => {
    loadHolidays();
  }, []);

  async function loadHolidays() {
    const { data } = await supabase.from("holidays").select("*").order("date");
    setHolidays(data || []);
    setLoading(false);
  }

  async function addHoliday() {
    if (!form.date || !form.name) {
      setMsg("Date and name are required.");
      return;
    }
    const { error } = await supabase.from("holidays").insert({
      date: form.date,
      name: form.name,
      hours: parseFloat(form.hours),
    });
    if (error) {
      setMsg(error.message);
      return;
    }
    const holidayDate = new Date(form.date + "T12:00:00");
    const yr = holidayDate.getFullYear();
    const mo = holidayDate.getMonth() + 1;
    const { data: employees } = await supabase
      .from("profiles")
      .select("id")
      .eq("status", "active");
    const { data: holidayProj } = await supabase
      .from("projects")
      .select("id")
      .eq("code", "HOLIDAY")
      .single();
    if (employees && holidayProj) {
      for (const emp of employees) {
        let { data: tc } = await supabase
          .from("timecards")
          .select("id,status")
          .eq("employee_id", emp.id)
          .eq("year", yr)
          .eq("month", mo)
          .maybeSingle();
        if (!tc) {
          const { data: newTc } = await supabase
            .from("timecards")
            .insert({
              employee_id: emp.id,
              year: yr,
              month: mo,
              status: "unapproved",
            })
            .select()
            .single();
          tc = newTc;
        }
        if (tc && tc.status !== "approved") {
          await supabase.from("timecard_entries").insert({
            timecard_id: tc.id,
            entry_date: form.date,
            hours: parseFloat(form.hours),
            project_id: holidayProj.id,
          });
        }
      }
    }
    setMsg("Holiday added and applied to all active employees!");
    setForm({ date: "", name: "", hours: "8" });
    loadHolidays();
    setTimeout(() => setMsg(""), 2000);
  }

  async function deleteHoliday(id) {
    await supabase.from("holidays").delete().eq("id", id);
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  }

  return (
    <div className="card">
      <div className="card-title">Company Holidays</div>
      <p
        style={{
          fontSize: 12,
          color: "var(--gray-500)",
          marginBottom: 16,
          lineHeight: 1.6,
        }}
      >
        Holidays automatically appear on employee timecards under the HOLIDAY
        project code. Employees cannot edit or delete holiday entries.
      </p>
      {msg && <div className="success">{msg}</div>}
      <div
        style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}
      >
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          style={{
            padding: "7px 10px",
            border: "1px solid var(--border)",
            borderRadius: 2,
            fontFamily: "var(--font)",
            fontSize: 13,
          }}
        />
        <input
          placeholder="Holiday Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          style={{
            flex: 1,
            minWidth: 160,
            padding: "7px 10px",
            border: "1px solid var(--border)",
            borderRadius: 2,
            fontFamily: "var(--font)",
            fontSize: 13,
          }}
        />
        <select
          value={form.hours}
          onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
          style={{
            padding: "7px 10px",
            border: "1px solid var(--border)",
            borderRadius: 2,
            fontFamily: "var(--font)",
            fontSize: 13,
          }}
        >
          {[4, 8].map((h) => (
            <option key={h} value={h}>
              {h}h
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={addHoliday}>
          Add Holiday
        </button>
      </div>
      {loading ? (
        <div className="loading" style={{ padding: 20 }}>
          Loading…
        </div>
      ) : holidays.length === 0 ? (
        <div className="empty" style={{ padding: 20 }}>
          No holidays configured.
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Hours</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id}>
                  <td>{h.date}</td>
                  <td>{h.name}</td>
                  <td>{h.hours}h</td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => deleteHoliday(h.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── EDITABLE CELL ──────────────────────────────────────────
function EditableCell({ value, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  if (editing)
    return (
      <div style={{ display: "flex", gap: 4 }}>
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          style={{
            width: 100,
            padding: "3px 6px",
            border: "1px solid var(--amber)",
            fontFamily: "var(--mono)",
            fontSize: 12,
          }}
        />
        <button
          className="btn btn-sm btn-success"
          onClick={() => {
            onSave(val);
            setEditing(false);
          }}
        >
          ✓
        </button>
        <button className="btn btn-sm" onClick={() => setEditing(false)}>
          ✕
        </button>
      </div>
    );
  return (
    <span
      style={{
        cursor: "pointer",
        fontSize: 12,
        color: value ? "var(--ink)" : "var(--muted)",
      }}
      onClick={() => setEditing(true)}
    >
      {value || placeholder} ✎
    </span>
  );
}
